import React, { useState, useEffect } from 'react';
import { useAuth } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import '../../styles/responsive-admin.css';
import { 
  Shield, 
  LogOut, 
  Plus, 
  Users, 
  BookOpen, 
  Settings,
  UserCheck,
  Trash2,
  Edit,
  FileText,
  Bold,
  Italic,
  Underline,
  Image as ImageIcon,
  X,
  Upload,
  Pause,
  Play,
  Key,
  Lock,
  Unlock,
  KeyRound,
  ChevronDown,
  Calendar,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Download,
  Mail,
  MessageSquare,
  Save,
  Info,
  Send,
  Search,
  PlusCircle,
  Edit3
} from 'lucide-react';
import axios from 'axios';
import ChangePasswordModal from '../common/ChangePasswordModal';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';
  // Default tab: 'users' for admin, 'exams' for moderator
  const [activeTab, setActiveTab] = useState(isAdmin ? 'users' : 'exams');
  const [examActiveTab, setExamActiveTab] = useState('writing'); // For exam management sub-tabs
  const [writingSubTab, setWritingSubTab] = useState('overview'); // 'overview' or 'partial-manage'
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isCreateBatchOpen, setIsCreateBatchOpen] = useState(false);
  const [isAssignUsersOpen, setIsAssignUsersOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchUsers, setBatchUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    designation: ''
  });
  const [newBatch, setNewBatch] = useState({
    name: '',
    description: ''
  });
  const [userAssignments, setUserAssignments] = useState([]);
  const [isCreateTestOpen, setIsCreateTestOpen] = useState(false);
  const [isEditTestOpen, setIsEditTestOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    profile_picture: '',
    designation: '',
    bio: '',
    phone: ''
  });
  const [writingTests, setWritingTests] = useState([]);
  const [submissionsSummary, setSubmissionsSummary] = useState([]);
  const [submissionsTestTypeFilter, setSubmissionsTestTypeFilter] = useState('all');
  const [submissionOverviewTab, setSubmissionOverviewTab] = useState('partial'); // 'partial' or 'class'
  const [classTestInstructorFilter, setClassTestInstructorFilter] = useState('all'); // Filter for class test tab
  const [isSubmissionsModalOpen, setIsSubmissionsModalOpen] = useState(false);
  const [isPendingSubmissionsModalOpen, setIsPendingSubmissionsModalOpen] = useState(false);
  const [allPendingSubmissions, setAllPendingSubmissions] = useState([]);
  
  // Settings
  const [systemSettings, setSystemSettings] = useState({ inspect_element_protection: false });
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [moderatorCanDelete, setModeratorCanDelete] = useState(true);
  const [moderatorCanViewFailedEmails, setModeratorCanViewFailedEmails] = useState(true);
  const [instructorsList, setInstructorsList] = useState([]);
  
  // Failed Emails state
  const [failedEmails, setFailedEmails] = useState([]);
  const [loadingFailedEmails, setLoadingFailedEmails] = useState(false);
  const [selectedFailedEmails, setSelectedFailedEmails] = useState([]);
  const [resendingEmails, setResendingEmails] = useState(false);
  
  // Activity Logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [logStartDate, setLogStartDate] = useState('');
  const [logEndDate, setLogEndDate] = useState('');
  
  // Instructor Activity Logs (for Moderators)
  const [instructorActivityLogs, setInstructorActivityLogs] = useState([]);
  const [instructorLogStartDate, setInstructorLogStartDate] = useState('');
  const [instructorLogEndDate, setInstructorLogEndDate] = useState('');
  const [selectedInstructorForLog, setSelectedInstructorForLog] = useState('all');
  const [reportSubTab, setReportSubTab] = useState('summary'); // 'summary' or 'instructor_activity'
  
  // Report & Summary
  const [reportData, setReportData] = useState({
    totalPartialTests: 0,
    totalClassTests: 0,
    totalPendingSubmissions: 0,
    overduePendingSubmissions: [],
    todaysSubmissions: 0
  });
  const [isTodaysSubmissionsModalOpen, setIsTodaysSubmissionsModalOpen] = useState(false);
  const [todaysSubmissionsList, setTodaysSubmissionsList] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedTestSubmissions, setSelectedTestSubmissions] = useState(null);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('all');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isDeleteTestOpen, setIsDeleteTestOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isBulkRegisterOpen, setIsBulkRegisterOpen] = useState(false);
  const [isStudentListOpen, setIsStudentListOpen] = useState(false);
  const [isBatchSettingsOpen, setIsBatchSettingsOpen] = useState(false);
  const [batchStudents, setBatchStudents] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [bulkCSV, setBulkCSV] = useState('');
  const [credits, setCredits] = useState({
    listening: 0,
    reading: 0,
    writing: 0,
    speaking: 0,
    mock: 0
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [portalValidity, setPortalValidity] = useState('');
  const [newTest, setNewTest] = useState({
    title: '',
    task1_prompt: '',
    task1_image: null,
    task1_image_width: 500,
    task1_image_height: 300,
    task2_prompt: '',
    duration_minutes: 60,
    assigned_batches: [],
    assigned_students: [],
    assigned_instructor: '',
    is_public: false,
    status: 'draft'
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [assignmentType, setAssignmentType] = useState('batches'); // 'batches' or 'students'

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Check user status periodically - logout if user is paused/deactivated
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const response = await axios.get('/auth/me');
        if (!response.data.is_active) {
          toast.error('Your account has been paused by admin');
          logout();
        }
      } catch (error) {
        // If unauthorized, user will be logged out automatically by axios interceptor
        console.error('Failed to check user status:', error);
      }
    };

    // Check immediately on mount
    checkUserStatus();

    // Check every 5 minutes (reduced from 30s to lower server load)
    const interval = setInterval(checkUserStatus, 300000);

    return () => clearInterval(interval);
  }, [logout]);

  useEffect(() => {
    fetchModeratorPermissions(); // Always fetch moderator permissions
    if (activeTab === 'exams') {
      if (examActiveTab === 'writing') {
        fetchWritingTests();
        fetchSubmissionsSummary();
      }
    } else if (activeTab === 'settings') {
      fetchSettings();
      fetchBlockedIPs();
      fetchActivityLogs();
    } else if (activeTab === 'reports') {
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, examActiveTab, writingSubTab]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [usersRes, batchesRes] = await Promise.all([
        axios.get('/admin/users'),
        axios.get('/admin/batches')
      ]);
      
      setUsers(usersRes.data);
      setBatches(batchesRes.data);
      
      // Filter instructors for assignment dropdown
      const instructors = usersRes.data.filter(u => u.role === 'instructor');
      setInstructorsList(instructors);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWritingTests = async () => {
    try {
      const response = await axios.get('/instructor/tests');
      setWritingTests(response.data);
    } catch (error) {
      console.error('Failed to fetch tests:', error);
    }
  };

  const fetchSubmissionsSummary = async () => {
    try {
      const response = await axios.get('/admin/submissions/summary');
      setSubmissionsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions summary:', error);
      toast.error('Failed to fetch submissions data');
    }
  };

  const handleDeleteSubmission = async () => {
    if (!deletingSubmissionId) return;
    
    try {
      await axios.delete(`/admin/submissions/${deletingSubmissionId}`);
      toast.success('Submission deleted successfully');
      
      // Refresh submissions summary
      await fetchSubmissionsSummary();
      
      // Update current modal data if open
      if (selectedTestSubmissions) {
        const updatedTestData = submissionsSummary.find(
          t => t.test_id === selectedTestSubmissions.test_id
        );
        if (updatedTestData) {
          setSelectedTestSubmissions(updatedTestData);
        }
      }
      
      setIsDeleteConfirmOpen(false);
      setDeletingSubmissionId(null);
    } catch (error) {
      console.error('Failed to delete submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleToggleLock = async (testId, currentLockStatus) => {
    try {
      const response = await axios.put(`/admin/tests/${testId}/lock`);
      toast.success(response.data.message);
      await fetchWritingTests();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
      toast.error('Failed to update lock status');
    }
  };

  const handleAssignInstructor = async (testId, instructorId) => {
    try {
      const assignValue = instructorId === 'none' ? null : instructorId;
      await axios.put(`/admin/tests/${testId}/assign-instructor`, {
        assigned_instructor: assignValue
      });
      toast.success(assignValue ? 'Instructor assigned successfully!' : 'Instructor unassigned');
      await fetchSubmissionsSummary(); // Refresh to show updated instructor
    } catch (error) {
      console.error('Failed to assign instructor:', error);
      toast.error('Failed to assign instructor');
    }
  };

  const handleAssignInstructorToSubmission = async (submissionId, instructorId) => {
    try {
      const assignValue = instructorId === 'none' ? null : instructorId;
      await axios.put(`/admin/submissions/${submissionId}/assign-instructor`, {
        assigned_instructor: assignValue
      });
      toast.success(assignValue ? 'Instructor assigned to submission!' : 'Instructor unassigned from submission');
      await fetchSubmissionsSummary(); // Refresh submissions
    } catch (error) {
      console.error('Failed to assign instructor to submission:', error);
      toast.error('Failed to assign instructor to submission');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/admin/settings');
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchBlockedIPs = async () => {
    try {
      const response = await axios.get('/admin/blocked-ips');
      setBlockedIPs(response.data);
    } catch (error) {
      console.error('Failed to fetch blocked IPs:', error);
    }
  };

  const fetchModeratorPermissions = async () => {
    try {
      const response = await axios.get('/admin/settings/moderator-permissions');
      setModeratorCanDelete(response.data.can_delete_submissions);
      setModeratorCanViewFailedEmails(response.data.can_view_failed_emails !== false);
    } catch (error) {
      console.error('Failed to fetch moderator permissions:', error);
      setModeratorCanDelete(true); // Default to true on error
      setModeratorCanViewFailedEmails(true);
    }
  };

  const fetchFailedEmails = async () => {
    setLoadingFailedEmails(true);
    try {
      const response = await axios.get('/admin/failed-emails');
      setFailedEmails(response.data);
    } catch (error) {
      console.log('Could not fetch failed emails');
    } finally {
      setLoadingFailedEmails(false);
    }
  };

  const handleBulkResendEmails = async () => {
    if (selectedFailedEmails.length === 0) {
      toast.error('Please select at least one submission');
      return;
    }
    
    // Show warning for large bulk sends
    if (selectedFailedEmails.length > 10) {
      if (!window.confirm(`Sending ${selectedFailedEmails.length} emails may take ${Math.ceil(selectedFailedEmails.length * 2 / 60)} minutes. Continue?`)) {
        return;
      }
    }
    
    setResendingEmails(true);
    toast.info(`Sending ${selectedFailedEmails.length} emails... This may take a while.`);
    
    try {
      const response = await axios.post('/admin/bulk-resend-emails', selectedFailedEmails, {
        timeout: selectedFailedEmails.length * 10000 // 10 seconds per email timeout
      });
      const result = response.data;
      
      if (result.success > 0) {
        toast.success(`Successfully sent ${result.success} emails`);
      }
      if (result.rate_limited > 0) {
        toast.warning(`${result.rate_limited} emails hit rate limit - try again later`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to send ${result.failed} emails`);
      }
      
      // Refresh the list
      fetchFailedEmails();
      setSelectedFailedEmails([]);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.warning('Request timed out - emails are still being sent in background. Refresh to see status.');
      } else {
        toast.error('Failed to resend emails');
      }
    } finally {
      setResendingEmails(false);
    }
  };

  const handleToggleProtection = async (enabled) => {
    try {
      await axios.put('/admin/settings', {
        inspect_element_protection: enabled
      });
      setSystemSettings({ ...systemSettings, inspect_element_protection: enabled });
      toast.success(enabled ? 'Protection enabled!' : 'Protection disabled!');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleUnblockIP = async (ipAddress) => {
    try {
      await axios.delete(`/admin/blocked-ips/${ipAddress}`);
      toast.success('IP unblocked successfully!');
      fetchBlockedIPs();
    } catch (error) {
      console.error('Failed to unblock IP:', error);
      toast.error('Failed to unblock IP');
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const params = {};
      if (logStartDate) params.start_date = new Date(logStartDate).toISOString();
      if (logEndDate) params.end_date = new Date(logEndDate).toISOString();
      if (selectedInstructor !== 'all') params.user_id = selectedInstructor;
      if (selectedAction !== 'all') params.action = selectedAction;
      
      const response = await axios.get('/admin/activity-logs', { params });
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    }
  };

  const fetchReportData = async () => {
    try {
      const response = await axios.get('/admin/reports/summary');
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load report data');
    }
  };

  const fetchInstructorActivityLogs = async () => {
    try {
      const params = {};
      if (instructorLogStartDate) params.start_date = new Date(instructorLogStartDate).toISOString();
      if (instructorLogEndDate) params.end_date = new Date(instructorLogEndDate).toISOString();
      if (selectedInstructorForLog !== 'all') params.instructor_id = selectedInstructorForLog;
      
      const response = await axios.get('/admin/activity-logs/instructor-activities', { params });
      setInstructorActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch instructor activity logs:', error);
    }
  };

  const fetchAllPendingSubmissions = async () => {
    try {
      const response = await axios.get('/admin/submissions/all-pending');
      setAllPendingSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch all pending submissions:', error);
      toast.error('Failed to load pending submissions');
    }
  };

  const fetchTodaysSubmissions = async () => {
    try {
      const response = await axios.get('/admin/submissions/today');
      setTodaysSubmissionsList(response.data);
    } catch (error) {
      console.error('Failed to fetch today\'s submissions:', error);
      toast.error('Failed to load today\'s submissions');
    }
  };

  const formatAction = (action) => {
    const actionMap = {
      'created_test': 'Created Test',
      'edited_test': 'Edited Test',
      'deleted_test': 'Deleted Test',
      'deleted_submissions': 'Deleted All Submissions',
      'deleted_submission': 'Deleted Submission',
      'assigned_instructor': 'Assigned Instructor',
      'marked_submission': 'Marked Submission',
      'published_result': 'Published Result',
      'sent_pending_reminders': 'Sent Pending Reminders'
    };
    return actionMap[action] || action;
  };

  const handleDeleteTestSubmissions = async () => {
    if (!selectedTestSubmissions) return;
    
    const confirmMessage = `Are you sure you want to delete ALL submissions for "${selectedTestSubmissions.test_title}"?\n\nThis will delete ${selectedTestSubmissions.total_submissions} submission(s).\n\nNote: The test itself will remain in the test list.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      // Delete all submissions for this test
      const response = await axios.delete(`/admin/tests/${selectedTestSubmissions.test_id}/submissions`);
      toast.success('All submissions deleted successfully. The test remains in your test list.');
      
      // Refresh data
      await fetchSubmissionsSummary();
      
      setIsDeleteTestOpen(false);
      setSelectedTestSubmissions(null);
    } catch (error) {
      console.error('Failed to delete submissions:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete submissions';
      toast.error(errorMsg);
    }
  };

  const handleDeleteTest = async () => {
    if (!selectedTestSubmissions) return;
    
    try {
      await axios.delete(`/admin/tests/${selectedTestSubmissions.test_id}`);
      toast.success('Test deleted successfully. Student result links will remain active.');
      
      // Refresh data
      await fetchSubmissionsSummary();
      await fetchWritingTests();
      
      setIsDeleteTestOpen(false);
      setSelectedTestSubmissions(null);
    } catch (error) {
      console.error('Failed to delete test:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete test';
      toast.error(errorMsg);
    }
  };

  const handleDeletePartialTest = async (testId) => {
    try {
      // First check if there are pending submissions
      const response = await axios.get(`/admin/submissions/summary`);
      const testSubmission = response.data.find(t => t.test_id === testId);
      
      let confirmMessage = 'Are you sure you want to delete this test? This action cannot be undone.';
      
      if (testSubmission && testSubmission.pending_count > 0) {
        confirmMessage = `⚠️ WARNING: This test has ${testSubmission.pending_count} pending (unmarked) submission(s).\n\nDeleting this test will permanently remove it along with all its submissions.\n\nAre you absolutely sure you want to continue?`;
      }
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
      
      await axios.delete(`/admin/tests/${testId}`);
      toast.success('Test deleted successfully');
      
      // Refresh tests list
      await fetchWritingTests();
      await fetchSubmissionsSummary(); // Also refresh submission summary
    } catch (error) {
      console.error('Failed to delete test:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete test';
      toast.error(errorMsg);
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!newUser.name.trim()) {
      toast.error('Please enter user name');
      return;
    }
    if (!newUser.email.trim()) {
      toast.error('Please enter email address');
      return;
    }
    if (!newUser.password.trim()) {
      toast.error('Please enter password');
      return;
    }
    if (!newUser.role.trim()) {
      toast.error('Please select a role');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      await axios.post('/admin/users', newUser);
      toast.success('User created successfully!');
      setIsCreateUserOpen(false);
      setNewUser({ name: '', email: '', password: '', role: '', designation: '' });
      fetchInitialData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error('Failed to create user. Email might already exist.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.delete(`/admin/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setIsDeleteUserOpen(false);
      setSelectedUser(null);
      fetchInitialData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await axios.put(`/admin/users/${user.id}/profile`, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? 'User paused' : 'User activated');
      fetchInitialData();
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsResetPasswordOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      await axios.put(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setIsResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  // ============ REGISTERED STUDENTS FUNCTIONS ============
  const [registeredStudents, setRegisteredStudents] = useState([]);
  const [newStudent, setNewStudent] = useState({ name: '', email: '', phone: '', batch_number: '' });
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  const [bulkStudentText, setBulkStudentText] = useState('');
  const [editingRegisteredStudent, setEditingRegisteredStudent] = useState(null);
  const [isRegisteredStudentsOpen, setIsRegisteredStudentsOpen] = useState(false);

  const fetchRegisteredStudents = async (batchId) => {
    try {
      const response = await axios.get(`/admin/batches/${batchId}/students`);
      setRegisteredStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch registered students:', error);
      toast.error('Failed to fetch students');
    }
  };

  const handleAddStudent = async () => {
    if (!selectedBatch || !newStudent.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      await axios.post(`/admin/batches/${selectedBatch.id}/students`, {
        name: newStudent.name.trim(),
        email: newStudent.email.trim().toLowerCase(),
        phone: newStudent.phone.trim() || null,
        batch_number: newStudent.batch_number.trim() || null
      });
      toast.success('Student added successfully');
      setNewStudent({ name: '', email: '', phone: '', batch_number: '' });
      setIsAddStudentOpen(false);
      fetchRegisteredStudents(selectedBatch.id);
    } catch (error) {
      console.error('Failed to add student:', error);
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleBulkAddStudents = async () => {
    if (!selectedBatch || !bulkStudentText.trim()) {
      toast.error('Please enter student data');
      return;
    }

    // Parse CSV/text - format: name, email, phone (optional), batch_number (optional)
    const lines = bulkStudentText.trim().split('\n');
    const students = [];
    
    for (const line of lines) {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        students.push({
          name: parts[0],
          email: parts[1].toLowerCase(),
          phone: parts[2] || null,
          batch_number: parts[3] || null
        });
      }
    }

    if (students.length === 0) {
      toast.error('No valid students found. Format: Name, Email, Phone (optional), Batch No (optional)');
      return;
    }

    try {
      const response = await axios.post(`/admin/batches/${selectedBatch.id}/students/bulk`, {
        students
      });
      toast.success(response.data.message);
      setBulkStudentText('');
      setIsBulkAddOpen(false);
      fetchRegisteredStudents(selectedBatch.id);
    } catch (error) {
      console.error('Failed to bulk add students:', error);
      toast.error(error.response?.data?.detail || 'Failed to add students');
    }
  };

  const handleUpdateRegisteredStudent = async () => {
    if (!editingRegisteredStudent || !editingRegisteredStudent.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      await axios.put(
        `/admin/batches/${selectedBatch.id}/students/${editingRegisteredStudent.id}`,
        {
          name: editingRegisteredStudent.name.trim(),
          email: editingRegisteredStudent.email.trim().toLowerCase(),
          phone: editingRegisteredStudent.phone?.trim() || null,
          batch_number: editingRegisteredStudent.batch_number?.trim() || null
        }
      );
      toast.success('Student updated successfully');
      setEditingRegisteredStudent(null);
      fetchRegisteredStudents(selectedBatch.id);
    } catch (error) {
      console.error('Failed to update student:', error);
      toast.error(error.response?.data?.detail || 'Failed to update student');
    }
  };

  const handleDeleteRegisteredStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;

    try {
      await axios.delete(`/admin/batches/${selectedBatch.id}/students/${studentId}`);
      toast.success('Student deleted successfully');
      fetchRegisteredStudents(selectedBatch.id);
    } catch (error) {
      console.error('Failed to delete student:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const handleToggleBatchPause = async (batchId) => {
    try {
      const response = await axios.put(`/admin/batches/${batchId}/pause`);
      toast.success(response.data.message);
      fetchInitialData();
    } catch (error) {
      console.error('Failed to toggle batch pause:', error);
      toast.error(error.response?.data?.detail || 'Failed to update batch');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch? All registered students will also be deleted.')) return;

    try {
      await axios.delete(`/admin/batches/${batchId}`);
      toast.success('Batch deleted successfully');
      fetchInitialData();
    } catch (error) {
      console.error('Failed to delete batch:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const handleOpenBatchStudents = async (batch) => {
    setSelectedBatch(batch);
    await fetchRegisteredStudents(batch.id);
    setIsRegisteredStudentsOpen(true);
  };
  // ============ END REGISTERED STUDENTS FUNCTIONS ============

  const fetchBatchStudents = async (batchId) => {
    try {
      const response = await axios.get(`/admin/batches/${batchId}/users`);
      setBatchStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch batch students:', error);
      toast.error('Failed to fetch students');
    }
  };

  const handleStudentListOpen = async (batch) => {
    setSelectedBatch(batch);
    await fetchBatchStudents(batch.id);
    setIsStudentListOpen(true);
  };

  const handleUpdateBatchSettings = async () => {
    if (!portalValidity) {
      toast.error('Please enter portal validity date');
      return;
    }
    
    try {
      await axios.put(`/admin/batches/${selectedBatch.id}/settings`, {
        portal_validity: portalValidity
      });
      toast.success('Batch settings updated');
      setIsBatchSettingsOpen(false);
      setPortalValidity('');
      fetchInitialData();
    } catch (error) {
      console.error('Failed to update batch settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleRemoveStudentFromBatch = async (userId) => {
    if (!selectedBatch) return;
    
    try {
      // Use dedicated endpoint to remove user from batch
      await axios.delete(`/admin/batches/${selectedBatch.id}/users/${userId}`);
      
      toast.success('Student removed from batch');
      
      // Refresh batch students list
      const response = await axios.get(`/admin/batches/${selectedBatch.id}/users`);
      setBatchStudents(response.data);
      
      // Refresh overall data
      fetchInitialData();
    } catch (error) {
      console.error('Failed to remove student from batch:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove student from batch');
    }
  };

  const handleInstantLock = async (testId) => {
    try {
      const now = new Date().toISOString();
      await axios.put(`/instructor/tests/${testId}`, {
        ...writingTests.find(t => t.id === testId),
        end_date: now
      });
      toast.success('Test locked successfully!');
      fetchWritingTests();
    } catch (error) {
      console.error('Failed to lock test:', error);
      toast.error('Failed to lock test');
    }
  };

  const handleBulkRegister = async () => {
    if (!bulkCSV.trim()) {
      toast.error('Please enter CSV data');
      return;
    }
    if (!selectedBatch) {
      toast.error('Please select a batch');
      return;
    }

    // Parse CSV
    const lines = bulkCSV.trim().split('\n');
    const students = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const parts = line.split(',').map(p => p.trim());
      if (parts.length < 4) {
        toast.error(`Invalid CSV format at line ${i + 1}`);
        return;
      }
      
      students.push({
        email: parts[0],
        name: parts[1],
        phone: parts[2],
        password: parts[3]
      });
    }

    if (students.length === 0) {
      toast.error('No valid students found in CSV');
      return;
    }

    try {
      await axios.post('/admin/batches/bulk-register', {
        batch_id: selectedBatch.id,
        students: students,
        credits: credits
      });
      
      const totalCredits = Object.values(credits).reduce((a, b) => a + b, 0);
      toast.success(`${students.length} students registered with ${totalCredits} credits each!`);
      setIsBulkRegisterOpen(false);
      setBulkCSV('');
      setCredits({ listening: 0, reading: 0, writing: 0, speaking: 0, mock: 0 });
      fetchInitialData();
    } catch (error) {
      console.error('Failed to bulk register:', error);
      toast.error(error.response?.data?.detail || 'Failed to register students');
    }
  };

  const handleCreateBatch = async () => {
    try {
      await axios.post('/admin/batches', newBatch);
      toast.success('Batch created successfully!');
      setIsCreateBatchOpen(false);
      setNewBatch({ name: '', description: '' });
      fetchInitialData();
    } catch (error) {
      toast.error('Failed to create batch');
    }
  };

  const openProfileEdit = (user) => {
    setSelectedUser(user);
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      profile_picture: user.profile_picture || '',
      designation: user.designation || '',
      bio: user.bio || '',
      phone: user.phone || ''
    });
    setIsEditProfileOpen(true);
  };

  const handleProfileImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ ...profileData, profile_picture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async () => {
    if (!selectedUser) return;

    // Validation
    if (!profileData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    // Check if email is being changed
    const emailChanged = profileData.email && profileData.email !== selectedUser.email;
    if (emailChanged) {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      
      // Confirm email change
      if (!window.confirm(`Are you sure you want to change the email from "${selectedUser.email}" to "${profileData.email}"?\n\nA notification email will be sent to the new address.`)) {
        return;
      }
    }

    try {
      await axios.put(`/admin/users/${selectedUser.id}/profile`, profileData);
      if (emailChanged) {
        toast.success('Profile updated! Notification sent to new email.');
      } else {
        toast.success('Profile updated successfully!');
      }
      setIsEditProfileOpen(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewTest({ ...newTest, task1_image: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setNewTest({ 
      ...newTest, 
      task1_image: null,
      task1_image_width: 500,
      task1_image_height: 300
    });
    setImagePreview(null);
  };

  const formatText = (field, formatType) => {
    const textarea = document.querySelector(`textarea[name="${field}"]`);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    if (!selectedText) {
      toast.error('Please select text to format');
      return;
    }

    let formattedText = '';
    switch (formatType) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        formattedText = selectedText;
    }

    const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
    setNewTest({ ...newTest, [field]: newValue });
  };

  const handleCreateTest = async () => {
    // Validation
    if (!newTest.title.trim()) {
      toast.error('Please enter test title');
      return;
    }
    if (!newTest.task1_prompt.trim()) {
      toast.error('Please enter Task 1 prompt');
      return;
    }
    if (!newTest.task2_prompt.trim()) {
      toast.error('Please enter Task 2 prompt');
      return;
    }
    // Assignment validation - skip if test is public
    if (!newTest.is_public) {
      const hasBatchAssignment = newTest.assigned_batches.length > 0;
      const hasStudentAssignment = newTest.assigned_students.length > 0;
      
      if (!hasBatchAssignment && !hasStudentAssignment) {
        toast.error('Please assign to batches/students OR make test public');
        return;
      }
    }

    try {
      await axios.post('/instructor/tests', newTest);
      toast.success('Test created successfully!');
      setIsCreateTestOpen(false);
      setNewTest({
        title: '',
        task1_prompt: '',
        task1_image: null,
        task1_image_width: 500,
        task1_image_height: 300,
        task2_prompt: '',
        duration_minutes: 60,
        assigned_batches: [],
        assigned_students: [],
        is_public: false,
        status: 'draft'
      });
      setImagePreview(null);
      setAssignmentType('batches');
      fetchWritingTests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
    }
  };

  const openEditTest = (test) => {
    setSelectedTest(test);
    setNewTest({
      title: test.title,
      task1_prompt: test.task1_prompt,
      task1_image: test.task1_image,
      task1_image_width: test.task1_image_width || 500,
      task1_image_height: test.task1_image_height || 300,
      task2_prompt: test.task2_prompt,
      duration_minutes: test.duration_minutes,
      assigned_batches: test.assigned_batches || [],
      assigned_students: test.assigned_students || [],
      is_public: test.is_public || false,
      status: test.status
    });
    if (test.task1_image) {
      setImagePreview(test.task1_image);
    }
    setIsEditTestOpen(true);
  };

  const handleEditTest = async () => {
    // Same validation as create
    if (!newTest.title.trim()) {
      toast.error('Please enter test title');
      return;
    }
    if (!newTest.task1_prompt.trim()) {
      toast.error('Please enter Task 1 prompt');
      return;
    }
    if (!newTest.task2_prompt.trim()) {
      toast.error('Please enter Task 2 prompt');
      return;
    }
    if (!newTest.is_public) {
      const hasBatchAssignment = newTest.assigned_batches.length > 0;
      const hasStudentAssignment = newTest.assigned_students.length > 0;
      
      if (!hasBatchAssignment && !hasStudentAssignment) {
        toast.error('Please assign to batches/students OR make test public');
        return;
      }
    }

    try {
      await axios.put(`/instructor/tests/${selectedTest.id}`, newTest);
      toast.success('Test updated successfully!');
      setIsEditTestOpen(false);
      setSelectedTest(null);
      setNewTest({
        title: '',
        task1_prompt: '',
        task1_image: null,
        task1_image_width: 500,
        task1_image_height: 300,
        task2_prompt: '',
        duration_minutes: 60,
        assigned_batches: [],
        assigned_students: [],
        is_public: false,
        status: 'draft'
      });
      setImagePreview(null);
      setAssignmentType('batches');
      fetchWritingTests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update test');
    }
  };

  const fetchBatchUsers = async (batchId) => {
    try {
      const response = await axios.get(`/admin/batches/${batchId}/users`);
      setBatchUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch batch users');
    }
  };

  const handleAssignUsers = async () => {
    try {
      const selectedAssignments = userAssignments
        .filter(user => user.selected)
        .map(user => ({
          user_id: user.user_id,
          role: user.role
        }));
      
      await axios.post(`/admin/batches/${selectedBatch.id}/assign`, selectedAssignments);
      toast.success('Users assigned successfully!');
      
      // Clean up state
      setIsAssignUsersOpen(false);
      setUserAssignments([]);
      setSelectedBatch(null);
      setBatchUsers([]);
    } catch (error) {
      toast.error('Failed to assign users');
      console.error('Assignment error:', error);
    }
  };

  const openAssignUsersDialog = (batch) => {
    setSelectedBatch(batch);
    fetchBatchUsers(batch.id);
    setIsAssignUsersOpen(true);
    
    // Initialize assignments
    const assignments = users.map(user => ({
      user_id: user.id,
      name: user.name,
      email: user.email,
      current_role: user.role,
      role: user.role,
      selected: false
    }));
    setUserAssignments(assignments);
  };

  const toggleUserSelection = (userId, selected, role) => {
    setUserAssignments(prev => 
      prev.map(user => 
        user.user_id === userId 
          ? { ...user, selected, role: selected ? role : user.current_role }
          : user
      )
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeClass = (role) => {
    const roleClasses = {
      'admin': 'bg-red-500',
      'instructor': 'bg-blue-500',
      'student': 'bg-green-500'
    };
    return roleClasses[role] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/radiance-logo.webp" 
                alt="Radiance Education" 
                className="h-8 sm:h-10 md:h-12 w-auto"
              />
              <div>
                <h1 className="text-sm sm:text-lg md:text-xl font-heading font-bold text-white">Admin Dashboard</h1>
                <p className="text-xs sm:text-sm text-white opacity-80">Radiance Education</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Role Badge - Hidden on mobile */}
              <div className="hidden md:flex btn-gradient px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm items-center">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden lg:inline">{user.role === 'moderator' ? 'Moderator' : 'Admin'} - </span>
                {user.name}
              </div>
              
              {/* Profile Menu - Clickable Profile Picture */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-white shadow-lg hover:opacity-80 transition-opacity cursor-pointer bg-gradient-to-br from-purple-500 to-pink-500"
                >
                  {user.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </button>
                
                {showProfileMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40 bg-black bg-opacity-50 animate-fadeIn" 
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-2xl z-50 overflow-hidden animate-zoomIn">
                      {/* Close Button */}
                      <button
                        onClick={() => setShowProfileMenu(false)}
                        className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
                        aria-label="Close"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      
                      {/* Profile Header with large circle */}
                      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 text-center relative overflow-hidden">
                        {/* Background Image with low opacity */}
                        <div 
                          className="absolute inset-0 bg-cover bg-center opacity-15"
                          style={{
                            backgroundImage: 'url(/modal-bg.jpg)',
                            filter: 'blur(2px)'
                          }}
                        />
                        
                        {/* Content */}
                        <div className="relative z-10">
                          <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br from-purple-500 to-pink-500 mb-3">
                            {user.profile_picture ? (
                              <img 
                                src={user.profile_picture} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-3xl">
                                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            )}
                          </div>
                          <h2 className="text-xl font-bold text-white">{user.name}</h2>
                          <p className="text-sm text-white opacity-90">{user.email}</p>
                          <span className="inline-block mt-2 px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-medium text-white">
                            {user.role === 'moderator' ? 'Moderator' : 'Admin'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowChangePassword(true);
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-slate-100 rounded-lg flex items-center text-slate-700 transition-colors"
                        >
                          <KeyRound className="w-5 h-5 mr-3 text-purple-600" />
                          <span className="font-medium">Change Password</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            logout();
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-red-50 rounded-lg flex items-center text-red-600 transition-colors mt-1"
                        >
                          <LogOut className="w-5 h-5 mr-3" />
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation - Responsive */}
        <div className="flex flex-wrap bg-white rounded-lg p-1 mb-4 sm:mb-8 shadow-sm border border-slate-200 gap-1">
          {/* User Management - Admin only */}
          {isAdmin && (
            <button
              className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
                activeTab === 'users' 
                  ? 'bg-slate-100 text-slate-900 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('users')}
              data-testid="users-tab"
            >
              <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">User Management</span>
              <span className="sm:hidden">Users</span>
            </button>
          )}
          {/* HIDDEN: Student & Batch Management - Will be enabled when working on student portal */}
          {/* <button
            className={`flex items-center justify-center px-6 py-3 rounded-md transition-all flex-1 ${
              activeTab === 'batches' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('batches')}
            data-testid="batches-tab"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Student & Batch Management
          </button> */}
          <button
            className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
              activeTab === 'exams' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('exams')}
            data-testid="exams-tab"
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exam Management</span>
            <span className="sm:hidden">Exams</span>
          </button>
          <button
            className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
              activeTab === 'reports' 
                ? 'bg-slate-100 text-slate-900 font-semibold' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('reports')}
            data-testid="reports-tab"
          >
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Report & Summary</span>
            <span className="sm:hidden">Reports</span>
          </button>
          {/* Failed Emails tab - Admin and Moderator with permission */}
          {(isAdmin || (isModerator && moderatorCanViewFailedEmails)) && (
            <button
              className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
                activeTab === 'failed_emails' 
                  ? 'bg-slate-100 text-slate-900 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => { setActiveTab('failed_emails'); fetchFailedEmails(); }}
              data-testid="failed-emails-tab"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Failed Emails</span>
              <span className="sm:hidden">Emails</span>
              {failedEmails.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {failedEmails.length}
                </span>
              )}
            </button>
          )}
          {/* Settings tab - Admin only */}
          {isAdmin && (
            <button
              className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
                activeTab === 'settings' 
                  ? 'bg-slate-100 text-slate-900 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('settings')}
              data-testid="settings-tab"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Settings</span>
            </button>
          )}
          {/* Batches tab - Admin only */}
          {isAdmin && (
            <button
              className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-md transition-all flex-1 min-w-[120px] text-xs sm:text-base ${
                activeTab === 'batches' 
                  ? 'bg-slate-100 text-slate-900 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('batches')}
              data-testid="batches-tab"
            >
              <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Batches</span>
              <span className="sm:hidden">Batches</span>
            </button>
          )}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-slate-700">
                Users Management
              </h2>
              <Dialog open={isCreateUserOpen} onOpenChange={(open) => {
                setIsCreateUserOpen(open);
                if (!open) {
                  // Reset form when dialog closes
                  setNewUser({ name: '', email: '', password: '', role: '', designation: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary w-full sm:w-auto text-sm" size="sm" data-testid="create-user-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-card border-none max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-slate-700">Create New User</DialogTitle>
                    <DialogDescription className="text-slate-500">
                      Add a new user to the IELTS portal
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Full Name</Label>
                      <Input
                        placeholder="Enter full name"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        className="input-modern"
                        data-testid="user-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-700">Email</Label>
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        className="input-modern"
                        data-testid="user-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-700">Password</Label>
                      <Input
                        type="password"
                        placeholder="Enter password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        className="input-modern"
                        data-testid="user-password-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-700">Role</Label>
                      <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                        <SelectTrigger className="input-modern" data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="instructor">Instructor</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-700">
                        Designation <span className="text-slate-400 text-xs">(Optional, for Instructors)</span>
                      </Label>
                      <Input
                        placeholder="e.g., IELTS Instructor"
                        value={newUser.designation}
                        onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button onClick={handleCreateUser} className="btn-primary" data-testid="create-user-submit">
                      Create User
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Users List - Admins, Moderators & Instructors */}
            <div className="grid grid-cols-1 gap-4">
              {users
                .filter(user => user.role === 'admin' || user.role === 'moderator' || user.role === 'instructor')
                .map((user) => (
                <Card key={user.id} className="glass-card" data-testid={`user-${user.id}`}>
                  <CardContent className="py-6">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* Profile Picture */}
                      <div className="flex-shrink-0 self-center sm:self-start">
                        {user.profile_picture ? (
                          <img
                            src={user.profile_picture}
                            alt={user.name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                            <span className="text-xl font-bold text-white">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-semibold text-slate-700 break-words">
                              {user.name}
                              {/* Show instructor code for admin/moderator */}
                              {user.role === 'instructor' && user.instructor_code && (
                                <span className="ml-2 text-sm font-normal text-purple-600">({user.instructor_code})</span>
                              )}
                            </h3>
                            {user.designation && (
                              <p className="text-sm text-purple-600 font-medium break-words">{user.designation}</p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium text-white self-start ${
                            getRoleBadgeClass(user.role)
                          }`}>
                            {user.role.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-slate-500 mb-1 break-all">{user.email}</p>
                        {user.phone && (
                          <p className="text-sm text-slate-500 mb-2 break-words">{user.phone}</p>
                        )}
                        {user.bio && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2 break-words">{user.bio}</p>
                        )}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-1 sm:gap-0 text-sm text-slate-400 mt-3">
                          <span>Created: {formatDate(user.created_at)}</span>
                          <span className={user.is_active ? 'text-green-600' : 'text-red-500'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        {/* Test Creation Permission - Only for Instructors */}
                        {user.role === 'instructor' && (
                          <div className="flex items-center gap-2 mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                            <Checkbox
                              id={`can-create-${user.id}`}
                              checked={user.can_create_tests || false}
                              onCheckedChange={async (checked) => {
                                try {
                                  await axios.put(`/admin/users/${user.id}/profile`, {
                                    can_create_tests: checked
                                  });
                                  toast.success(checked ? 'Test creation enabled' : 'Test creation disabled');
                                  fetchInitialData();
                                } catch (error) {
                                  toast.error('Failed to update permission');
                                }
                              }}
                            />
                            <Label 
                              htmlFor={`can-create-${user.id}`}
                              className="text-sm text-slate-600 cursor-pointer flex items-center gap-1"
                            >
                              <FileText className="w-4 h-4 text-purple-500" />
                              Can Create Tests
                            </Label>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                        {/* Edit Profile */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openProfileEdit(user)}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          title="Edit Profile"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        {/* Toggle Active Status */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleUserStatus(user)}
                          className={user.is_active 
                            ? "text-orange-600 border-orange-300 hover:bg-orange-50" 
                            : "text-green-600 border-green-300 hover:bg-green-50"}
                          title={user.is_active ? "Pause User" : "Activate User"}
                        >
                          {user.is_active ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {/* Reset Password */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetPassword(user)}
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        
                        {/* Delete User */}
                        {/* Delete button - Admin only */}
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              setIsDeleteUserOpen(true);
                            }}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Batches Tab - Student Registration Management */}
        {activeTab === 'batches' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-slate-700">
                Batch & Student Registration
              </h2>
              <Dialog open={isCreateBatchOpen} onOpenChange={(open) => {
                setIsCreateBatchOpen(open);
                if (!open) setNewBatch({ name: '', description: '' });
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Batch
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-card border-none">
                  <DialogHeader>
                    <DialogTitle className="text-slate-700">Create New Batch</DialogTitle>
                    <DialogDescription className="text-slate-500">
                      Create a batch to organize registered students
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Batch Name*</Label>
                      <Input
                        placeholder="e.g., Batch 01, IELTS-2025"
                        value={newBatch.name}
                        onChange={(e) => setNewBatch({ ...newBatch, name: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Description</Label>
                      <Input
                        placeholder="Optional description"
                        value={newBatch.description}
                        onChange={(e) => setNewBatch({ ...newBatch, description: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateBatch} className="btn-primary">
                      Create Batch
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-semibold mb-1">Student Registration for Test Access</p>
                  <p>Register students with their email addresses. Only registered emails can access tests. Pause a batch to temporarily block all students in that batch from taking tests.</p>
                </div>
              </div>
            </div>

            {/* Batches Grid */}
            {batches.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No batches created yet. Create a batch to start registering students.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {batches.map((batch) => (
                  <Card key={batch.id} className={`glass-card ${batch.is_paused ? 'border-2 border-amber-400' : ''}`}>
                    <CardContent className="py-5">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                            {batch.name}
                            {batch.is_paused && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                PAUSED
                              </span>
                            )}
                          </h3>
                          {batch.description && (
                            <p className="text-sm text-slate-500 mt-1">{batch.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-slate-400 mb-4">
                        Created: {formatDate(batch.created_at)}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleOpenBatchStudents(batch)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Users className="w-3 h-3 mr-1" />
                          Students
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleBatchPause(batch.id)}
                          className={batch.is_paused ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}
                        >
                          {batch.is_paused ? <Play className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                          {batch.is_paused ? 'Resume' : 'Pause'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteBatch(batch.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Student List Dialog */}
            <Dialog open={isRegisteredStudentsOpen} onOpenChange={setIsRegisteredStudentsOpen}>
              <DialogContent className="glass-card border-none max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-slate-700 flex items-center justify-between">
                    <span>Registered Students - {selectedBatch?.name}</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => setIsAddStudentOpen(true)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setIsBulkAddOpen(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Bulk Add
                      </Button>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                {/* Students Table */}
                {registeredStudents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No students registered in this batch yet.</p>
                    <p className="text-sm mt-2">Click &quot;Add&quot; or &quot;Bulk Add&quot; to register students.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Phone</th>
                          <th className="px-3 py-2 text-left">Batch No</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registeredStudents.map((student, index) => (
                          <tr key={student.id} className="border-b hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                            <td className="px-3 py-2">{student.name}</td>
                            <td className="px-3 py-2 text-blue-600">{student.email}</td>
                            <td className="px-3 py-2 text-slate-500">{student.phone || '-'}</td>
                            <td className="px-3 py-2 text-slate-500">{student.batch_number || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingRegisteredStudent(student)}
                                  className="text-blue-600 hover:bg-blue-50"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteRegisteredStudent(student.id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="text-sm text-slate-500 mt-2">
                  Total: {registeredStudents.length} students
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Single Student Dialog */}
            <Dialog open={isAddStudentOpen} onOpenChange={setIsAddStudentOpen}>
              <DialogContent className="glass-card border-none">
                <DialogHeader>
                  <DialogTitle className="text-slate-700">Add Student</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Name</Label>
                    <Input
                      placeholder="Student name"
                      value={newStudent.name}
                      onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                      className="input-modern"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Email* (for verification)</Label>
                    <Input
                      type="email"
                      placeholder="student@email.com"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      className="input-modern"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Phone (optional)</Label>
                      <Input
                        placeholder="01712345678"
                        value={newStudent.phone}
                        onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Batch/Roll No (optional)</Label>
                      <Input
                        placeholder="001"
                        value={newStudent.batch_number}
                        onChange={(e) => setNewStudent({ ...newStudent, batch_number: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddStudentOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddStudent} className="btn-primary">Add Student</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Bulk Add Students Dialog */}
            <Dialog open={isBulkAddOpen} onOpenChange={setIsBulkAddOpen}>
              <DialogContent className="glass-card border-none max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-slate-700">Bulk Add Students</DialogTitle>
                  <DialogDescription className="text-slate-500">
                    Paste student data in CSV format: Name, Email, Phone (optional), Batch No (optional)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                    <p className="font-semibold mb-1">Format Example:</p>
                    <code className="block text-xs bg-white p-2 rounded border">
                      Rahim Khan, rahim@email.com, 01712345678, 001<br/>
                      Karim Ahmed, karim@email.com, , 002<br/>
                      Jabbar Ali, jabbar@email.com
                    </code>
                  </div>
                  <textarea
                    className="w-full h-48 p-3 border rounded-lg text-sm font-mono"
                    placeholder="Paste student data here..."
                    value={bulkStudentText}
                    onChange={(e) => setBulkStudentText(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsBulkAddOpen(false)}>Cancel</Button>
                  <Button onClick={handleBulkAddStudents} className="btn-primary">
                    <Upload className="w-4 h-4 mr-2" />
                    Add All Students
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Student Dialog */}
            <Dialog open={!!editingRegisteredStudent} onOpenChange={(open) => !open && setEditingRegisteredStudent(null)}>
              <DialogContent className="glass-card border-none">
                <DialogHeader>
                  <DialogTitle className="text-slate-700">Edit Student</DialogTitle>
                </DialogHeader>
                {editingRegisteredStudent && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Name</Label>
                      <Input
                        value={editingRegisteredStudent.name}
                        onChange={(e) => setEditingRegisteredStudent({ ...editingRegisteredStudent, name: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Email*</Label>
                      <Input
                        type="email"
                        value={editingRegisteredStudent.email}
                        onChange={(e) => setEditingRegisteredStudent({ ...editingRegisteredStudent, email: e.target.value })}
                        className="input-modern"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700">Phone</Label>
                        <Input
                          value={editingRegisteredStudent.phone || ''}
                          onChange={(e) => setEditingRegisteredStudent({ ...editingRegisteredStudent, phone: e.target.value })}
                          className="input-modern"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700">Batch/Roll No</Label>
                        <Input
                          value={editingRegisteredStudent.batch_number || ''}
                          onChange={(e) => setEditingRegisteredStudent({ ...editingRegisteredStudent, batch_number: e.target.value })}
                          className="input-modern"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingRegisteredStudent(null)}>Cancel</Button>
                  <Button onClick={handleUpdateRegisteredStudent} className="btn-primary">Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Exam Management Tab */}
        {activeTab === 'exams' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-slate-700">
                IELTS Exam Management
              </h2>
              <div className="text-sm text-slate-500">
                Manage all IELTS test modules
              </div>
            </div>

            {/* IELTS Module Sub-tabs - Responsive */}
            <div className="grid grid-cols-2 sm:flex bg-white rounded-lg p-1 mb-6 shadow-sm border border-slate-200 gap-1">
              <button
                className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                  examActiveTab === 'reading' 
                    ? 'bg-slate-100 text-slate-900 font-semibold' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => { setExamActiveTab('reading'); setWritingSubTab('overview'); }}
                data-testid="reading-exam-tab"
              >
                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Reading
              </button>
              <button
                className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                  examActiveTab === 'writing' 
                    ? 'bg-slate-100 text-slate-900 font-semibold' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => { setExamActiveTab('writing'); setWritingSubTab('overview'); }}
                data-testid="writing-exam-tab"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Writing
              </button>
              <button
                className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                  examActiveTab === 'listening' 
                    ? 'bg-slate-100 text-slate-900 font-semibold' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => { setExamActiveTab('listening'); setWritingSubTab('overview'); }}
                data-testid="listening-exam-tab"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Listening
              </button>
              <button
                className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                  examActiveTab === 'speaking' 
                    ? 'bg-slate-100 text-slate-900 font-semibold' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => { setExamActiveTab('speaking'); setWritingSubTab('overview'); }}
                data-testid="speaking-exam-tab"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Speaking
              </button>
            </div>

            {/* Writing Module Content */}
            {examActiveTab === 'writing' && (
              <div className="space-y-6">
                
                {/* Overview Section */}
                {writingSubTab === 'overview' && (
                  <>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-700">
                        Writing Test Management
                      </h3>
                      <Button className="btn-primary w-full sm:w-auto text-sm" size="sm" data-testid="create-writing-test-admin">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Writing Test
                      </Button>
                    </div>

                    {/* Writing Test Types Cards - Responsive */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {/* Partial Test */}
                      <Card className="glass-card">
                        <CardHeader>
                          <CardTitle className="text-lg text-slate-700 flex items-center">
                            <FileText className="w-5 h-5 mr-2 text-blue-500" />
                            Partial Test
                          </CardTitle>
                          <CardDescription className="text-slate-500">
                            Academic Writing Task 1 & 2 practice tests
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Active Tests:</span>
                              <span className="font-medium text-slate-700">2</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Total Submissions:</span>
                              <span className="font-medium text-slate-700">15</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Average Score:</span>
                              <span className="font-medium text-green-600">7.2</span>
                            </div>
                            <Button 
                              className="w-full btn-primary mt-4"
                              onClick={() => setWritingSubTab('partial-manage')}
                              data-testid="manage-partial-tests-btn"
                            >
                              Manage Tests
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                  {/* Test Submissions Summary */}
                  <div className="col-span-full">
                    <div className="mb-4">
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-700 mb-3 sm:mb-4">Test Submissions Overview</h3>
                      
                      {/* Test Type Tabs - Responsive */}
                      <div className="flex gap-2 bg-white rounded-lg p-2 shadow-sm">
                        <button
                          className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                            submissionOverviewTab === 'partial' 
                              ? 'bg-slate-100 text-slate-900 font-semibold' 
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={() => setSubmissionOverviewTab('partial')}
                        >
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Partial Test</span>
                          <span className="sm:hidden">Partial</span>
                        </button>
                        <button
                          className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-2.5 rounded-md transition-all flex-1 text-xs sm:text-base ${
                            submissionOverviewTab === 'class' 
                              ? 'bg-slate-100 text-slate-900 font-semibold' 
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={() => setSubmissionOverviewTab('class')}
                        >
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Class Test</span>
                          <span className="sm:hidden">Class</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Instructor Filter for Class Test Tab - Responsive */}
                    {submissionOverviewTab === 'class' && (
                      <div className="mb-4">
                        <Label className="text-xs sm:text-sm text-slate-700 mb-2">Filter by Instructor:</Label>
                        <Select 
                          value={classTestInstructorFilter} 
                          onValueChange={setClassTestInstructorFilter}
                        >
                          <SelectTrigger className="w-full sm:w-64 h-9">
                            <SelectValue placeholder="All Instructors" />
                          </SelectTrigger>
                          <SelectContent className="max-w-[90vw] sm:max-w-xs">
                            <SelectItem value="all">All Instructors</SelectItem>
                            {users
                              .filter(u => u.role === 'instructor')
                              .map(instructor => (
                                <SelectItem key={instructor.id} value={instructor.id}>
                                  <span className="truncate block">{instructor.name}</span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {(() => {
                        // Filter submissions by selected tab
                        const testTypeFilter = submissionOverviewTab === 'partial' ? 'partial_test' : 'class_test';
                        let filteredTests = submissionsSummary.filter(
                          t => t.test_type === testTypeFilter
                        );
                        
                        // Apply instructor filter for class test tab
                        if (submissionOverviewTab === 'class' && classTestInstructorFilter !== 'all') {
                          filteredTests = filteredTests.filter(
                            t => t.created_by === classTestInstructorFilter
                          );
                        }
                        
                        // Sort by created_at date (newest first)
                        filteredTests.sort((a, b) => {
                          const dateA = new Date(a.created_at || 0);
                          const dateB = new Date(b.created_at || 0);
                          return dateB - dateA; // Descending order (newest first)
                        });
                        
                        return filteredTests.length === 0 ? (
                          <div className="col-span-full text-center text-slate-500 py-8">
                            {submissionOverviewTab === 'partial' 
                              ? 'কোনো Partial Test সাবমিশন পাওয়া যায়নি' 
                              : 'কোনো Class Test সাবমিশন পাওয়া যায়নি'}
                          </div>
                        ) : (
                          filteredTests.map((testData) => (
                          <Card key={testData.test_id} className="glass-card">
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <CardTitle className="text-base text-slate-700 flex items-center">
                                    {testData.test_type === 'partial_test' ? (
                                      <FileText className="w-4 h-4 mr-2" style={{color: 'rgb(22, 113, 127)'}} />
                                    ) : testData.test_type === 'class_test' ? (
                                      <Users className="w-4 h-4 mr-2" style={{color: '#E53935'}} />
                                    ) : (
                                      <BookOpen className="w-4 h-4 mr-2" style={{color: '#2E7D32'}} />
                                    )}
                                    <span className="truncate" title={testData.test_title}>
                                      {testData.test_title}
                                    </span>
                                  </CardTitle>
                                  <CardDescription className="text-xs text-slate-500">
                                    {testData.test_type === 'partial_test' ? 'Partial Test' : 
                                     testData.test_type === 'class_test' ? 'Class Test' : 'Batch Homework'}
                                  </CardDescription>
                                </div>
                                {/* Show Creator Name for Class Tests */}
                                {testData.test_type === 'class_test' && testData.created_by_name && (
                                  <div className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded ml-2">
                                    <span className="font-medium">Created by:</span>
                                    <br />
                                    <span className="text-slate-700">{testData.created_by_name}</span>
                                  </div>
                                )}
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {/* Assign Instructor - for all test types */}
                                <div className="mb-3">
                                  <Label className="text-xs text-slate-600 mb-1">Assign to Instructor:</Label>
                                  <Select 
                                    value={testData.assigned_instructor || 'none'} 
                                    onValueChange={(value) => handleAssignInstructor(testData.test_id, value)}
                                  >
                                    <SelectTrigger className="w-full text-sm h-9">
                                      <SelectValue>
                                        {testData.assigned_instructor && testData.assigned_instructor !== 'none' ? (
                                          <span className="flex items-center gap-2 truncate">
                                            <span className="truncate">{users.find(u => u.id === testData.assigned_instructor)?.name || 'Assigned'}</span>
                                            <span className="text-green-600 font-bold flex-shrink-0">✓</span>
                                          </span>
                                        ) : (
                                          'None'
                                        )}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="max-w-[90vw] sm:max-w-xs">
                                      <SelectItem value="none">None</SelectItem>
                                      {users.filter(u => u.role === 'instructor').map(instructor => (
                                        <SelectItem key={instructor.id} value={instructor.id} className="cursor-pointer">
                                          <span className="truncate block">{instructor.name}</span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                {testData.assigned_instructor_name && (
                                  <div className="text-xs text-slate-600 mb-2 p-2 bg-slate-50 rounded">
                                    <span className="font-medium">Instructor:</span>{' '}
                                    <span className="text-slate-700">{testData.assigned_instructor_name}</span>
                                  </div>
                                )}
                                {testData.batch_names.length > 0 && (
                                  <div className="text-xs text-slate-600 mb-2">
                                    <span className="font-medium">Batches:</span>{' '}
                                    <span className="text-slate-700">{testData.batch_names.join(', ')}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-sm border-t pt-2">
                                  <span className="text-slate-600">Total:</span>
                                  <span className="font-medium text-slate-700">{testData.total_submissions}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Published:</span>
                                  <span className="font-medium" style={{color: '#2E7D32'}}>
                                    {testData.published_count}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Pending:</span>
                                  <span className="font-medium text-yellow-600">
                                    {testData.pending_count}
                                  </span>
                                </div>
                                <div className="flex gap-2 mt-3">
                                  <Button 
                                    className="flex-1 btn-primary text-sm"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedTestSubmissions(testData);
                                      setIsSubmissionsModalOpen(true);
                                    }}
                                  >
                                    View Details
                                  </Button>
                                  {/* Delete submissions button - Admin or Moderator (if enabled) */}
                                  {(isAdmin || (isModerator && moderatorCanDelete)) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-300 hover:bg-red-50"
                                      onClick={() => {
                                        setSelectedTestSubmissions(testData);
                                        setIsDeleteTestOpen(true);
                                      }}
                                      title="Delete Submissions"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                          ))
                        );
                      })()}
                    </div>
                  </div>
                </div>

                    {/* Recent Writing Activity - HIDDEN */}
                    {/* <Card className="glass-card">
                      <CardHeader>
                        <CardTitle className="text-lg text-slate-700">Recent Writing Activity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                              <div>
                                <p className="font-medium text-slate-700">Writing Test - Screenshots Demo</p>
                                <p className="text-sm text-slate-500">Created by instructor@ielts.com • Active</p>
                              </div>
                            </div>
                            <div className="text-sm text-slate-500">
                              Today, 12:50 PM
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                              <div>
                                <p className="font-medium text-slate-700">IELTS Writing Partial Test 01</p>
                                <p className="text-sm text-slate-500">Created by instructor@ielts.com • Active</p>
                              </div>
                            </div>
                            <div className="text-sm text-slate-500">
                              Today, 11:30 AM
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                              <div>
                                <p className="font-medium text-slate-700">Student submission marked</p>
                                <p className="text-sm text-slate-500">John Doe • Final Band: 7.5</p>
                              </div>
                            </div>
                            <div className="text-sm text-slate-500">
                              Today, 10:15 AM
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card> */}
                  </>
                )}

                {/* Partial Test Management Section */}
                {writingSubTab === 'partial-manage' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Button
                          onClick={() => setWritingSubTab('overview')}
                          variant="outline"
                          size="sm"
                          className="text-slate-600 text-xs sm:text-sm"
                        >
                          ← Back
                        </Button>
                        <h3 className="text-base sm:text-xl font-semibold text-slate-700">
                          Partial Test - Create & Manage
                        </h3>
                      </div>
                      <Dialog open={isCreateTestOpen} onOpenChange={setIsCreateTestOpen}>
                        <DialogTrigger asChild>
                          <Button className="btn-primary w-full sm:w-auto text-sm" size="sm" data-testid="create-partial-test-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Partial Test
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass-card border-none max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-slate-700">Create New Partial Test</DialogTitle>
                            <DialogDescription className="text-slate-500">
                              Create a new writing test with Task 1 & Task 2
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-slate-700">Test Title*</Label>
                              <Input
                                placeholder="e.g., IELTS Academic Writing Practice 01"
                                value={newTest.title}
                                onChange={(e) => setNewTest({ ...newTest, title: e.target.value })}
                                className="input-modern"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 1 Question*</Label>
                              <div className="flex gap-2 mb-2">
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'bold')}
                                  className="text-slate-600"
                                  title="Bold (Select text first)"
                                >
                                  <Bold className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'italic')}
                                  className="text-slate-600"
                                  title="Italic (Select text first)"
                                >
                                  <Italic className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'underline')}
                                  className="text-slate-600"
                                  title="Underline (Select text first)"
                                >
                                  <Underline className="w-4 h-4" />
                                </Button>
                              </div>
                              <textarea
                                name="task1_prompt"
                                placeholder="Enter Task 1 question (Academic Writing Task 1 - 150 words)&#10;&#10;Tip: Select text and use formatting buttons above. Use **text** for bold, *text* for italic"
                                value={newTest.task1_prompt}
                                onChange={(e) => setNewTest({ ...newTest, task1_prompt: e.target.value })}
                                className="input-modern min-h-[120px]"
                                rows={5}
                              />
                            </div>

                            {/* Task 1 Image Upload */}
                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 1 Image (Optional)</Label>
                              {!imagePreview ? (
                                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    id="image-upload"
                                  />
                                  <label htmlFor="image-upload" className="cursor-pointer">
                                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                                    <p className="text-sm text-slate-600">Click to upload image</p>
                                    <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 5MB</p>
                                  </label>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="relative border rounded-lg p-4 bg-slate-50">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={removeImage}
                                      className="absolute top-2 right-2 text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                    <img 
                                      src={imagePreview} 
                                      alt="Task 1 preview"
                                      style={{
                                        width: `${newTest.task1_image_width}px`,
                                        height: `${newTest.task1_image_height}px`,
                                        objectFit: 'contain'
                                      }}
                                      className="mx-auto"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs text-slate-600">Width (px)</Label>
                                      <Input
                                        type="number"
                                        value={newTest.task1_image_width}
                                        onChange={(e) => setNewTest({ ...newTest, task1_image_width: parseInt(e.target.value) || 500 })}
                                        className="input-modern"
                                        min="100"
                                        max="1200"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-slate-600">Height (px)</Label>
                                      <Input
                                        type="number"
                                        value={newTest.task1_image_height}
                                        onChange={(e) => setNewTest({ ...newTest, task1_image_height: parseInt(e.target.value) || 300 })}
                                        className="input-modern"
                                        min="100"
                                        max="800"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 2 Question*</Label>
                              <div className="flex gap-2 mb-2">
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'bold')}
                                  className="text-slate-600"
                                  title="Bold (Select text first)"
                                >
                                  <Bold className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'italic')}
                                  className="text-slate-600"
                                  title="Italic (Select text first)"
                                >
                                  <Italic className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'underline')}
                                  className="text-slate-600"
                                  title="Underline (Select text first)"
                                >
                                  <Underline className="w-4 h-4" />
                                </Button>
                              </div>
                              <textarea
                                name="task2_prompt"
                                placeholder="Enter Task 2 question (Academic Writing Task 2 - 250 words)&#10;&#10;Tip: Select text and use formatting buttons above"
                                value={newTest.task2_prompt}
                                onChange={(e) => setNewTest({ ...newTest, task2_prompt: e.target.value })}
                                className="input-modern min-h-[120px]"
                                rows={5}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-slate-700">Duration (minutes)</Label>
                                <Input
                                  type="number"
                                  placeholder="60"
                                  value={newTest.duration_minutes}
                                  onChange={(e) => setNewTest({ ...newTest, duration_minutes: parseInt(e.target.value) || 60 })}
                                  className="input-modern"
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="text-slate-700">Status</Label>
                                <Select value={newTest.status} onValueChange={(value) => setNewTest({ ...newTest, status: value })}>
                                  <SelectTrigger className="input-modern">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Public Link Toggle */}
                            <div className="space-y-2 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    checked={newTest.is_public}
                                    onCheckedChange={(checked) => setNewTest({ ...newTest, is_public: checked })}
                                  />
                                  <Label className="text-slate-700 font-medium cursor-pointer">
                                    Make test publicly accessible via link
                                  </Label>
                                </div>
                              </div>
                              {newTest.is_public && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <p className="text-sm text-blue-800">
                                    ℹ️ Anyone with the link can take this test without logging in. 
                                    Link will be generated after creating the test.
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="space-y-3">
                              <Label className="text-slate-700">Assign Test{!newTest.is_public && '*'}</Label>
                              
                              {/* Toggle between Batches and Students */}
                              <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                                <Button
                                  type="button"
                                  variant={assignmentType === 'batches' ? 'default' : 'ghost'}
                                  className={`flex-1 ${assignmentType === 'batches' ? 'bg-white shadow-sm' : ''}`}
                                  onClick={() => setAssignmentType('batches')}
                                >
                                  <Users className="w-4 h-4 mr-2" />
                                  Batches
                                </Button>
                                <Button
                                  type="button"
                                  variant={assignmentType === 'students' ? 'default' : 'ghost'}
                                  className={`flex-1 ${assignmentType === 'students' ? 'bg-white shadow-sm' : ''}`}
                                  onClick={() => setAssignmentType('students')}
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Students
                                </Button>
                              </div>

                              {/* Batches List */}
                              {assignmentType === 'batches' && (
                                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                                  {batches.map((batch) => (
                                    <div key={batch.id} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={newTest.assigned_batches.includes(batch.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setNewTest({
                                              ...newTest,
                                              assigned_batches: [...newTest.assigned_batches, batch.id]
                                            });
                                          } else {
                                            setNewTest({
                                              ...newTest,
                                              assigned_batches: newTest.assigned_batches.filter(id => id !== batch.id)
                                            });
                                          }
                                        }}
                                      />
                                      <Label className="text-slate-700 font-normal cursor-pointer">
                                        {batch.name}
                                      </Label>
                                    </div>
                                  ))}
                                  {batches.length === 0 && (
                                    <p className="text-sm text-slate-500">No batches available. Create batches first.</p>
                                  )}
                                </div>
                              )}

                              {/* Students List */}
                              {assignmentType === 'students' && (
                                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                                  {users.filter(u => u.role === 'student').map((student) => (
                                    <div key={student.id} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={newTest.assigned_students.includes(student.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setNewTest({
                                              ...newTest,
                                              assigned_students: [...newTest.assigned_students, student.id]
                                            });
                                          } else {
                                            setNewTest({
                                              ...newTest,
                                              assigned_students: newTest.assigned_students.filter(id => id !== student.id)
                                            });
                                          }
                                        }}
                                      />
                                      <div className="flex-1">
                                        <Label className="text-slate-700 font-normal cursor-pointer block">
                                          {student.name}
                                        </Label>
                                        <p className="text-xs text-slate-500">{student.email}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {users.filter(u => u.role === 'student').length === 0 && (
                                    <p className="text-sm text-slate-500">No students available. Create students first.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button onClick={handleCreateTest} className="btn-primary">
                              Create Test
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* Edit Test Dialog */}
                      <Dialog open={isEditTestOpen} onOpenChange={setIsEditTestOpen}>
                        <DialogContent className="glass-card border-none max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-slate-700">Edit Partial Test</DialogTitle>
                            <DialogDescription className="text-slate-500">
                              Update test details, prompts, and assignments
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-slate-700">Test Title*</Label>
                              <Input
                                placeholder="e.g., IELTS Academic Writing Practice 01"
                                value={newTest.title}
                                onChange={(e) => setNewTest({ ...newTest, title: e.target.value })}
                                className="input-modern"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 1 Question*</Label>
                              <div className="flex gap-2 mb-2">
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'bold')}
                                  className="text-slate-600"
                                  title="Bold (Select text first)"
                                >
                                  <Bold className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'italic')}
                                  className="text-slate-600"
                                  title="Italic (Select text first)"
                                >
                                  <Italic className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task1_prompt', 'underline')}
                                  className="text-slate-600"
                                  title="Underline (Select text first)"
                                >
                                  <Underline className="w-4 h-4" />
                                </Button>
                              </div>
                              <textarea
                                name="task1_prompt"
                                placeholder="Enter Task 1 question (Academic Writing Task 1 - 150 words)"
                                value={newTest.task1_prompt}
                                onChange={(e) => setNewTest({ ...newTest, task1_prompt: e.target.value })}
                                className="input-modern min-h-[120px]"
                                rows={5}
                              />
                            </div>

                            {/* Task 1 Image Upload */}
                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 1 Image (Optional)</Label>
                              {!imagePreview ? (
                                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    id="edit-image-upload"
                                  />
                                  <label htmlFor="edit-image-upload" className="cursor-pointer">
                                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                                    <p className="text-sm text-slate-600">Click to upload image</p>
                                    <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 5MB</p>
                                  </label>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="relative border rounded-lg p-4 bg-slate-50">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={removeImage}
                                      className="absolute top-2 right-2 text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                    <img 
                                      src={imagePreview} 
                                      alt="Task 1 preview"
                                      style={{
                                        width: `${newTest.task1_image_width}px`,
                                        height: `${newTest.task1_image_height}px`,
                                        objectFit: 'contain'
                                      }}
                                      className="mx-auto"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs text-slate-600">Width (px)</Label>
                                      <Input
                                        type="number"
                                        value={newTest.task1_image_width}
                                        onChange={(e) => setNewTest({ ...newTest, task1_image_width: parseInt(e.target.value) || 500 })}
                                        className="input-modern"
                                        min="100"
                                        max="1200"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-slate-600">Height (px)</Label>
                                      <Input
                                        type="number"
                                        value={newTest.task1_image_height}
                                        onChange={(e) => setNewTest({ ...newTest, task1_image_height: parseInt(e.target.value) || 300 })}
                                        className="input-modern"
                                        min="100"
                                        max="800"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label className="text-slate-700">Task 2 Question*</Label>
                              <div className="flex gap-2 mb-2">
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'bold')}
                                  className="text-slate-600"
                                  title="Bold (Select text first)"
                                >
                                  <Bold className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'italic')}
                                  className="text-slate-600"
                                  title="Italic (Select text first)"
                                >
                                  <Italic className="w-4 h-4" />
                                </Button>
                                <Button 
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => formatText('task2_prompt', 'underline')}
                                  className="text-slate-600"
                                  title="Underline (Select text first)"
                                >
                                  <Underline className="w-4 h-4" />
                                </Button>
                              </div>
                              <textarea
                                name="task2_prompt"
                                placeholder="Enter Task 2 question (Opinion Essay - 250 words)"
                                value={newTest.task2_prompt}
                                onChange={(e) => setNewTest({ ...newTest, task2_prompt: e.target.value })}
                                className="input-modern min-h-[120px]"
                                rows={5}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-slate-700">Assign Instructor for Marking (Optional)</Label>
                              <select
                                value={newTest.assigned_instructor || ''}
                                onChange={(e) => setNewTest({ ...newTest, assigned_instructor: e.target.value })}
                                className="w-full p-2 border rounded-md text-slate-700"
                              >
                                <option value="">-- No specific instructor --</option>
                                {users.filter(u => u.role === 'instructor').map((instructor) => (
                                  <option key={instructor.id} value={instructor.id}>
                                    {instructor.name} ({instructor.email})
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-slate-500">
                                If assigned, only this instructor can mark submissions for this test
                              </p>
                            </div>

                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="edit-is-public"
                                checked={newTest.is_public}
                                onCheckedChange={(checked) => setNewTest({ ...newTest, is_public: checked })}
                              />
                              <Label htmlFor="edit-is-public" className="text-slate-700">
                                Make this test public (accessible via shareable link)
                              </Label>
                            </div>

                            {/* Assign Test Section (Batches/Students) */}
                            <div className="space-y-3 border-t pt-4">
                              <Label className="text-slate-700">Assign Test{!newTest.is_public && '*'}</Label>

                              {/* Toggle between Batches and Students */}
                              <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                                <Button
                                  type="button"
                                  variant={assignmentType === 'batches' ? 'default' : 'ghost'}
                                  className={`flex-1 ${assignmentType === 'batches' ? 'bg-white shadow-sm' : ''}`}
                                  onClick={() => setAssignmentType('batches')}
                                  data-testid="edit-test-toggle-batches-btn"
                                >
                                  <Users className="w-4 h-4 mr-2" />
                                  Batches
                                </Button>
                                <Button
                                  type="button"
                                  variant={assignmentType === 'students' ? 'default' : 'ghost'}
                                  className={`flex-1 ${assignmentType === 'students' ? 'bg-white shadow-sm' : ''}`}
                                  onClick={() => setAssignmentType('students')}
                                  data-testid="edit-test-toggle-students-btn"
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Students
                                </Button>
                              </div>

                              {/* Batches List */}
                              {assignmentType === 'batches' && (
                                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto" data-testid="edit-test-batch-list">
                                  {batches.map((batch) => (
                                    <div key={batch.id} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={newTest.assigned_batches.includes(batch.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setNewTest({
                                              ...newTest,
                                              assigned_batches: [...newTest.assigned_batches, batch.id]
                                            });
                                          } else {
                                            setNewTest({
                                              ...newTest,
                                              assigned_batches: newTest.assigned_batches.filter(id => id !== batch.id)
                                            });
                                          }
                                        }}
                                        data-testid={`edit-test-batch-checkbox-${batch.id}`}
                                      />
                                      <Label className="text-slate-700 font-normal cursor-pointer">
                                        {batch.name}
                                      </Label>
                                    </div>
                                  ))}
                                  {batches.length === 0 && (
                                    <p className="text-sm text-slate-500">No batches available. Create batches first.</p>
                                  )}
                                </div>
                              )}

                              {/* Students List */}
                              {assignmentType === 'students' && (
                                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto" data-testid="edit-test-student-list">
                                  {users.filter(u => u.role === 'student').map((student) => (
                                    <div key={student.id} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={newTest.assigned_students.includes(student.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setNewTest({
                                              ...newTest,
                                              assigned_students: [...newTest.assigned_students, student.id]
                                            });
                                          } else {
                                            setNewTest({
                                              ...newTest,
                                              assigned_students: newTest.assigned_students.filter(id => id !== student.id)
                                            });
                                          }
                                        }}
                                        data-testid={`edit-test-student-checkbox-${student.id}`}
                                      />
                                      <div className="flex-1">
                                        <Label className="text-slate-700 font-normal cursor-pointer block">
                                          {student.name}
                                        </Label>
                                        <p className="text-xs text-slate-500">{student.email}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {users.filter(u => u.role === 'student').length === 0 && (
                                    <p className="text-sm text-slate-500">No students available. Create students first.</p>
                                  )}
                                </div>
                              )}

                              {newTest.is_public && (
                                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                                  ℹ️ Public link is enabled. Batch/student assignment is optional but still recorded for tracking.
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button onClick={handleEditTest} className="btn-primary" data-testid="edit-test-update-btn">
                              Update Test
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Test List Card */}
                    <Card className="glass-card">
                      <CardHeader>
                        <CardTitle className="text-lg text-slate-700">All Partial Tests</CardTitle>
                        <CardDescription className="text-slate-500">
                          Manage your partial writing tests (Task 1 & Task 2)
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {writingTests.filter(test => test.type === 'partial_test').length === 0 ? (
                            <div className="text-center py-12">
                              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-500">No partial tests created yet</p>
                              <p className="text-sm text-slate-400 mt-1">Click &quot;Create Partial Test&quot; to get started</p>
                            </div>
                          ) : (
                            writingTests.filter(test => test.type === 'partial_test').map((test) => (
                              <div key={test.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                    <h4 className="font-semibold text-slate-700 break-words">{test.title}</h4>
                                    <span className={`status-badge self-start sm:self-auto ${test.status === 'active' ? 'status-completed' : 'status-pending'}`}>
                                      {test.status.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="text-sm text-slate-500 space-y-1">
                                    <p className="break-words">Duration: {test.duration_minutes} minutes • 2 Tasks</p>
                                    <p className="break-words">
                                      Assigned to: 
                                      {test.assigned_batches?.length > 0 && ` ${test.assigned_batches.length} batch(es)`}
                                      {test.assigned_students?.length > 0 && ` ${test.assigned_students.length} student(s)`}
                                      {test.is_public && ' 🌐 Public'}
                                      {(!test.assigned_batches?.length && !test.assigned_students?.length && !test.is_public) && ' None'}
                                    </p>
                                    {test.is_public && test.public_link && (
                                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                                        <code className="bg-slate-200 px-2 py-1 rounded text-xs break-all overflow-wrap-anywhere flex-1">
                                          {window.location.origin}/test/{test.public_link}
                                        </code>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}/test/${test.public_link}`);
                                            toast.success('Link copied!');
                                          }}
                                          className="text-xs w-full sm:w-auto"
                                        >
                                          Copy
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-row sm:flex-wrap sm:w-auto sm:ml-4">
                                  <Button 
                                    variant="outline"
                                    size="sm" 
                                    className="text-slate-600 text-xs"
                                    onClick={() => openEditTest(test)}
                                  >
                                    <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outline"
                                    size="sm" 
                                    className="text-slate-600 text-xs"
                                    onClick={() => window.location.href = `/test-preview/${test.id}`}
                                  >
                                    View
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className={`text-xs ${test.is_locked ? "text-red-600 border-red-300" : "text-green-600 border-green-300"}`}
                                    onClick={() => handleToggleLock(test.id, test.is_locked)}
                                    title={test.is_locked ? "Unlock Test" : "Lock Test"}
                                  >
                                    {test.is_locked ? (
                                      <><Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />Lock Now</>
                                    ) : (
                                      <><Unlock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />Unlocked</>
                                    )}
                                  </Button>
                                  {/* Delete button - Admin only */}
                                  {isAdmin && (
                                    <Button 
                                      variant="outline"
                                      size="sm" 
                                      className="text-red-500 hover:bg-red-50 text-xs"
                                      onClick={() => handleDeletePartialTest(test.id)}
                                      title="Delete Test"
                                    >
                                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      Delete
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Other Modules - Coming Soon */}
            {examActiveTab === 'reading' && (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Reading Module</h3>
                <p className="text-slate-500">Coming soon in Phase 2</p>
              </div>
            )}
            
            {examActiveTab === 'listening' && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Listening Module</h3>
                <p className="text-slate-500">Coming soon in Phase 2</p>
              </div>
            )}
            
            {examActiveTab === 'speaking' && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Speaking Module</h3>
                <p className="text-slate-500">Coming soon in Phase 2</p>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-heading font-bold text-slate-700">System Settings</h2>
            
            {/* Inspect Element Protection */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Security Settings</CardTitle>
                <CardDescription>Configure security features for the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">Inspect Element Protection</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      When enabled, DevTools, Inspect Element, and Right-click will be disabled site-wide
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.inspect_element_protection}
                      onChange={(e) => handleToggleProtection(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Login Attempt Limit */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mt-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-slate-700">Login Attempt Limit</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Lock account for 7 hours after 3 failed login attempts within 2 minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.login_attempt_limit !== false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            login_attempt_limit: newValue
                          });
                          setSystemSettings({ ...systemSettings, login_attempt_limit: newValue });
                          toast.success(newValue ? 'Login attempt limit enabled!' : 'Login attempt limit disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Result Page Signature Style */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Result Page Settings</CardTitle>
                <CardDescription>Configure how the instructor signature appears on result pages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-3">Signature Style</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    Choose how instructor information is displayed on student result pages
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                      <input
                        type="radio"
                        name="signature_style"
                        value="name"
                        checked={systemSettings.signature_style !== 'code'}
                        onChange={async () => {
                          try {
                            await axios.put('/admin/settings', {
                              ...systemSettings,
                              signature_style: 'name'
                            });
                            setSystemSettings({ ...systemSettings, signature_style: 'name' });
                            toast.success('Signature style updated to Instructor Name');
                          } catch (error) {
                            toast.error('Failed to update setting');
                          }
                        }}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-slate-700">Instructor Name</p>
                        <p className="text-sm text-slate-500">Shows instructor's full name with signature style</p>
                        <div className="mt-2 p-2 bg-white border rounded text-center text-sm">
                          <p className="text-slate-500 text-xs mb-1">Marked by:</p>
                          <p className="text-lg italic text-teal-700 border-b pb-1" style={{fontFamily: 'cursive'}}>Mustakim Ahmed</p>
                          <p className="text-xs text-slate-400 mt-1">(IELTS Instructor - Radiance Education)</p>
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                      <input
                        type="radio"
                        name="signature_style"
                        value="code"
                        checked={systemSettings.signature_style === 'code'}
                        onChange={async () => {
                          try {
                            await axios.put('/admin/settings', {
                              ...systemSettings,
                              signature_style: 'code'
                            });
                            setSystemSettings({ ...systemSettings, signature_style: 'code' });
                            toast.success('Signature style updated to Instructor Code');
                          } catch (error) {
                            toast.error('Failed to update setting');
                          }
                        }}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-slate-700">Instructor Code</p>
                        <p className="text-sm text-slate-500">Shows anonymous code to protect instructor identity</p>
                        <div className="mt-2 p-2 bg-white border rounded text-center text-sm">
                          <p className="text-slate-500 text-xs mb-1">Marked by:</p>
                          <p className="font-semibold text-slate-700">Instructor Code: 8814</p>
                          <div className="border-b my-1"></div>
                          <p className="text-xs text-slate-400">IELTS Instructor — Radiance Education</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instructor Code Management */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Instructor Code Management</CardTitle>
                <CardDescription>
                  Generate unique codes for instructors who don't have one yet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> New instructors get codes automatically. Use this button only for existing instructors who were created before this feature was added.
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    if (window.confirm('This will assign codes to all instructors who don\'t have one. Continue?')) {
                      try {
                        const response = await axios.post('/admin/assign-instructor-codes');
                        toast.success(response.data.message);
                        // Refresh users data
                        const usersRes = await axios.get('/admin/users');
                        setUsers(usersRes.data);
                        const instructors = usersRes.data.filter(u => u.role === 'instructor');
                        setInstructorsList(instructors);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to assign codes');
                      }
                    }
                  }}
                  className="w-full"
                  variant="outline"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Assign Codes to Existing Instructors
                </Button>
              </CardContent>
            </Card>

            {/* Blocked IPs List */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Blocked IP Addresses</CardTitle>
                <CardDescription>
                  {blockedIPs.length} IP address(es) currently blocked
                </CardDescription>
              </CardHeader>
              <CardContent>
                {blockedIPs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No blocked IPs</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {blockedIPs.map((ip) => (
                      <div key={ip.ip_address} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex-1">
                          <p className="font-mono font-semibold text-slate-800">{ip.ip_address}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Blocked: {new Date(ip.blocked_at).toLocaleString('en-GB')} • 
                            Expires: {new Date(ip.expires_at).toLocaleString('en-GB')}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{ip.reason}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to unblock ${ip.ip_address}?`)) {
                              handleUnblockIP(ip.ip_address);
                            }
                          }}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PDF Download Setting */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Feedback Page Settings</CardTitle>
                <CardDescription>
                  Control PDF download option on student feedback pages
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-700">Enable PDF Download</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Allow students to download their feedback as PDF from result page
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.pdf_download_enabled || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            pdf_download_enabled: newValue
                          });
                          setSystemSettings({ ...systemSettings, pdf_download_enabled: newValue });
                          toast.success(newValue ? 'PDF download enabled!' : 'PDF download disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Moderator Settings */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">Moderator Settings</CardTitle>
                <CardDescription>
                  Control moderator permissions and account preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Welcome Email Setting */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-slate-700">Send Welcome Email to Moderators</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Automatically send welcome email with credentials when a new moderator is created
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.moderator_welcome_email !== false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            moderator_welcome_email: newValue
                          });
                          setSystemSettings({ ...systemSettings, moderator_welcome_email: newValue });
                          toast.success(newValue ? 'Moderator welcome email enabled!' : 'Moderator welcome email disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Submission Deletion Permission Setting */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-slate-700">Allow Moderators to Delete Submissions</p>
                        <p className="text-sm text-slate-500 mt-1">
                          When enabled, moderators can delete test submissions. When disabled, delete buttons will be hidden for moderators.
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.moderator_can_delete_submissions !== false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            moderator_can_delete_submissions: newValue
                          });
                          setSystemSettings({ ...systemSettings, moderator_can_delete_submissions: newValue });
                          toast.success(newValue ? 'Moderators can now delete submissions!' : 'Moderator deletion disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Moderator Failed Emails Permission */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-slate-700">Allow Moderators to View Failed Emails</p>
                        <p className="text-sm text-slate-500 mt-1">
                          When enabled, moderators can access the Failed Emails tab and resend failed notification emails.
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.moderator_can_view_failed_emails !== false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            moderator_can_view_failed_emails: newValue
                          });
                          setSystemSettings({ ...systemSettings, moderator_can_view_failed_emails: newValue });
                          setModeratorCanViewFailedEmails(newValue);
                          toast.success(newValue ? 'Moderators can now view failed emails!' : 'Moderator access to failed emails disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </CardContent>
            </Card>
            
            {/* Email Notifications */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700">📧 Email Notifications</CardTitle>
                <CardDescription>
                  Control email notifications for instructors and moderators
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Instructor Assignment Email Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-700">Instructor Assignment Notifications</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Send email when instructors are assigned or unassigned to tests/submissions
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.instructor_assignment_email !== false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            instructor_assignment_email: newValue
                          });
                          setSystemSettings({ ...systemSettings, instructor_assignment_email: newValue });
                          toast.success(newValue ? 'Instructor assignment emails enabled!' : 'Instructor assignment emails disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Send Test Email Button */}
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Send className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-slate-700">Test Email Template</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Send a test email to preview the new compact template design
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-100 ml-4"
                    onClick={async () => {
                      try {
                        const response = await axios.post('/admin/send-test-email');
                        if (response.data.success) {
                          toast.success(response.data.message);
                        } else {
                          toast.error(response.data.message || 'Failed to send test email');
                        }
                      } catch (error) {
                        console.error('Failed to send test email:', error);
                        toast.error(error.response?.data?.detail || 'Failed to send test email');
                      }
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Batch Email Verification */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
                  <span className="text-2xl">🔐</span> Batch Email Verification
                </CardTitle>
                <CardDescription>
                  Control access to tests by verifying student emails against registered batches
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-slate-700">Email Verification</p>
                        <p className="text-sm text-slate-500 mt-1">
                          When ON, only registered student emails can access tests
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.batch_email_verification || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            batch_email_verification: newValue
                          });
                          setSystemSettings({ ...systemSettings, batch_email_verification: newValue });
                          toast.success(newValue ? 'Email verification enabled! Only registered students can take tests.' : 'Email verification disabled! Anyone can take tests.');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* Status Info */}
                <div className={`p-3 rounded-lg border ${systemSettings.batch_email_verification ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-sm ${systemSettings.batch_email_verification ? 'text-green-700' : 'text-amber-700'}`}>
                    {systemSettings.batch_email_verification 
                      ? '✅ Verification is ON - Only registered student emails can access tests. Manage students in the Batches tab.'
                      : '⚠️ Verification is OFF - Anyone with the test link can take tests.'}
                  </p>
                </div>

                {/* Selective Verification Batches (only relevant when main toggle is OFF) */}
                {!systemSettings.batch_email_verification && (
                  <div className="border-t pt-4 space-y-3" data-testid="selective-verification-section">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-700">Selective Verification Batches</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Even when the main toggle is OFF, tests assigned to <strong>these selected batches</strong> will still require email verification. All other tests will be public.
                        </p>
                      </div>
                    </div>

                    <div className="border rounded-lg p-3 space-y-2 max-h-[220px] overflow-y-auto bg-slate-50" data-testid="selective-batch-list">
                      {batches.length === 0 ? (
                        <p className="text-sm text-slate-500">No batches available. Create batches first in the Batches tab.</p>
                      ) : (
                        batches.map((batch) => {
                          const selectedIds = systemSettings.verification_required_batch_ids || [];
                          const isChecked = selectedIds.includes(batch.id);
                          return (
                            <div key={batch.id} className="flex items-center space-x-2">
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={async (checked) => {
                                  const current = systemSettings.verification_required_batch_ids || [];
                                  const updated = checked
                                    ? [...current, batch.id]
                                    : current.filter(id => id !== batch.id);
                                  try {
                                    await axios.put('/admin/settings', {
                                      ...systemSettings,
                                      verification_required_batch_ids: updated
                                    });
                                    setSystemSettings({ ...systemSettings, verification_required_batch_ids: updated });
                                    toast.success(checked
                                      ? `Verification enabled for batch "${batch.name}"`
                                      : `Verification removed for batch "${batch.name}"`);
                                  } catch (error) {
                                    console.error('Failed to update selective verification:', error);
                                    toast.error('Failed to update setting');
                                  }
                                }}
                                data-testid={`selective-batch-checkbox-${batch.id}`}
                              />
                              <Label className="text-slate-700 font-normal cursor-pointer">
                                {batch.name}
                              </Label>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {(systemSettings.verification_required_batch_ids || []).length > 0 && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <p className="text-sm text-indigo-800">
                          🛡️ <strong>{(systemSettings.verification_required_batch_ids || []).length} batch(es)</strong> require verification. Tests assigned to these batches will check email registration even though main verification is OFF.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Hard Delete Setting */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
                  <span className="text-2xl">🗑️</span> Submission Delete Mode
                </CardTitle>
                <CardDescription>
                  Control how submissions are deleted from the system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-slate-700">Hard Delete Mode</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {systemSettings.hard_delete_enabled 
                            ? 'ON: Submissions permanently deleted (result links will NOT work)'
                            : 'OFF: Soft delete - submissions hidden but result links remain valid'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.hard_delete_enabled || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            hard_delete_enabled: newValue
                          });
                          setSystemSettings({ ...systemSettings, hard_delete_enabled: newValue });
                          toast.success(newValue ? '⚠️ Hard Delete enabled - Deletions are permanent!' : '✅ Soft Delete enabled - Result links preserved!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                  </label>
                </div>
                
                <div className={`p-3 rounded-lg border ${systemSettings.hard_delete_enabled ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <p className={`text-sm ${systemSettings.hard_delete_enabled ? 'text-red-700' : 'text-green-700'}`}>
                    {systemSettings.hard_delete_enabled
                      ? '⚠️ CAUTION: Hard Delete is ON - All deleted submissions will be permanently removed. Students will NOT be able to view their results!'
                      : '✅ Soft Delete is ON - Deleted submissions are hidden from admin but students can still view their results for 7 days.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Auto-Delete Published Submissions */}
            <Card className="glass-card" data-testid="auto-delete-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
                  <span className="text-2xl">🧹</span> Auto-Delete Old Published Submissions
                </CardTitle>
                <CardDescription>
                  Automatically remove published submissions older than N days. Keeps your database lean and fast.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Toggle */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-lg border border-sky-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-sky-600" />
                      <div>
                        <p className="font-medium text-slate-700">Auto-Delete Enabled</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {systemSettings.auto_delete_published_enabled
                            ? `ON: Published submissions older than ${systemSettings.auto_delete_published_days || 14} days will be deleted at 3:00 AM (BD time) every day.`
                            : 'OFF: Published submissions will be kept indefinitely.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      data-testid="auto-delete-toggle"
                      className="sr-only peer"
                      checked={systemSettings.auto_delete_published_enabled || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            ...systemSettings,
                            auto_delete_published_enabled: newValue,
                            auto_delete_published_days: systemSettings.auto_delete_published_days || 14
                          });
                          setSystemSettings({ ...systemSettings, auto_delete_published_enabled: newValue });
                          toast.success(newValue
                            ? `🧹 Auto-delete enabled. Cleanup runs daily at 3 AM (BD time).`
                            : '✅ Auto-delete disabled. Submissions will be kept.');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
                  </label>
                </div>

                {/* Days input */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                    Delete after
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    data-testid="auto-delete-days-input"
                    value={systemSettings.auto_delete_published_days ?? 14}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isFinite(v)) {
                        setSystemSettings({ ...systemSettings, auto_delete_published_days: v });
                      }
                    }}
                    onBlur={async (e) => {
                      const v = Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 14));
                      try {
                        await axios.put('/admin/settings', {
                          ...systemSettings,
                          auto_delete_published_days: v
                        });
                        setSystemSettings({ ...systemSettings, auto_delete_published_days: v });
                        toast.success(`Cleanup window set to ${v} days.`);
                      } catch (error) {
                        toast.error('Failed to update days');
                      }
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-slate-600">days from result publish date</span>
                </div>

                {/* Preview + manual run */}
                <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                  <p className="text-sm text-amber-800 mb-2">
                    💡 <strong>Tip:</strong> Use "Preview Cleanup" to see how many submissions will be deleted before the next scheduled run. Use "Run Cleanup Now" to trigger it immediately (the toggle must be ON).
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="auto-delete-preview-btn"
                      onClick={async () => {
                        try {
                          const r = await axios.get('/admin/auto-delete-preview');
                          toast.info(
                            <div>
                              <div className="font-semibold mb-1">Cleanup Preview</div>
                              <div className="text-xs">
                                Status: {r.data.enabled ? '🟢 ON' : '🔴 OFF'} • Window: {r.data.days} days
                              </div>
                              <div className="text-xs mt-1">
                                <strong>{r.data.submissions_eligible_for_deletion}</strong> submissions eligible for deletion right now
                              </div>
                            </div>,
                            { duration: 6000 }
                          );
                        } catch (error) {
                          toast.error('Failed to fetch preview');
                        }
                      }}
                    >
                      🔍 Preview Cleanup
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!systemSettings.auto_delete_published_enabled}
                      data-testid="auto-delete-run-now-btn"
                      onClick={async () => {
                        if (!window.confirm('Run cleanup NOW? This will permanently delete eligible published submissions. Continue?')) return;
                        try {
                          await axios.post('/admin/auto-delete-run-now');
                          toast.success('Cleanup triggered. Check Activity Logs for results.');
                        } catch (error) {
                          toast.error('Failed to run cleanup');
                        }
                      }}
                    >
                      ⚡ Run Cleanup Now
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp Integration */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
                  <span className="text-2xl">📱</span> WhatsApp Integration
                </CardTitle>
                <CardDescription>
                  Configure Meta WhatsApp Business API for instant notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-slate-700">WhatsApp Notifications</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Send assignment updates via WhatsApp
                        </p>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={systemSettings.whatsapp_notifications_enabled || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        try {
                          await axios.put('/admin/settings', {
                            whatsapp_notifications_enabled: newValue
                          });
                          setSystemSettings({ ...systemSettings, whatsapp_notifications_enabled: newValue });
                          toast.success(newValue ? 'WhatsApp notifications enabled!' : 'WhatsApp notifications disabled!');
                        } catch (error) {
                          console.error('Failed to update setting:', error);
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                {/* API Credentials */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Key className="w-4 h-4 text-slate-600" />
                    <h4 className="font-medium text-slate-700">API Credentials</h4>
                  </div>

                  {/* Phone Number ID */}
                  <div>
                    <Label className="text-sm text-slate-600 mb-2">Phone Number ID</Label>
                    <Input
                      type="text"
                      placeholder="Enter Meta WhatsApp Phone Number ID"
                      value={systemSettings.whatsapp_phone_number_id || ''}
                      onChange={(e) => setSystemSettings({ ...systemSettings, whatsapp_phone_number_id: e.target.value })}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      From: Meta Developer Portal → WhatsApp → Configuration
                    </p>
                  </div>

                  {/* Access Token */}
                  <div>
                    <Label className="text-sm text-slate-600 mb-2">Access Token</Label>
                    <Input
                      type="password"
                      placeholder="Enter Meta WhatsApp Access Token (EAAxxxx...)"
                      value={systemSettings.whatsapp_access_token || ''}
                      onChange={(e) => setSystemSettings({ ...systemSettings, whatsapp_access_token: e.target.value })}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      From: Business Settings → System Users → Generate Token
                    </p>
                  </div>

                  {/* Business Account ID */}
                  <div>
                    <Label className="text-sm text-slate-600 mb-2">Business Account ID (Optional)</Label>
                    <Input
                      type="text"
                      placeholder="Enter WhatsApp Business Account ID"
                      value={systemSettings.whatsapp_business_account_id || ''}
                      onChange={(e) => setSystemSettings({ ...systemSettings, whatsapp_business_account_id: e.target.value })}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Optional: From WhatsApp Manager → Settings
                    </p>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put('/admin/settings', {
                          whatsapp_notifications_enabled: systemSettings.whatsapp_notifications_enabled,
                          whatsapp_phone_number_id: systemSettings.whatsapp_phone_number_id,
                          whatsapp_access_token: systemSettings.whatsapp_access_token,
                          whatsapp_business_account_id: systemSettings.whatsapp_business_account_id
                        });
                        toast.success('WhatsApp credentials saved successfully!');
                      } catch (error) {
                        console.error('Failed to save credentials:', error);
                        toast.error('Failed to save credentials');
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save WhatsApp Configuration
                  </Button>

                  {/* Test WhatsApp Button */}
                  <div className="mt-3 flex gap-2">
                    <Input
                      type="tel"
                      placeholder="01XXXXXXXXX"
                      maxLength={11}
                      id="test-whatsapp-number"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        const phoneInput = document.getElementById('test-whatsapp-number');
                        const phone = phoneInput?.value;
                        if (!phone || phone.length !== 11) {
                          toast.error('Enter a valid 11-digit WhatsApp number');
                          return;
                        }
                        try {
                          toast.loading('Sending test message...', { id: 'test-wa' });
                          await axios.post(`/admin/test-whatsapp?phone_number=${phone}`);
                          toast.success('Test message sent! Check your WhatsApp.', { id: 'test-wa' });
                        } catch (error) {
                          const msg = error.response?.data?.detail || 'Failed to send';
                          toast.error(msg, { id: 'test-wa' });
                        }
                      }}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Test WhatsApp
                    </Button>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    <strong>Setup Guide:</strong> Follow Meta WhatsApp Business API setup to get credentials. Once configured, enable notifications above.
                  </p>
                </div>
              </CardContent>
            </Card>

          {/* Activity Log Section */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg text-slate-700">Recent Activity Log</CardTitle>
              <CardDescription>
                View instructor activities from last 2 months
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Start Date */}
                <div>
                  <Label className="text-sm text-slate-600 mb-1">Start Date</Label>
                  <Input 
                    type="date" 
                    value={logStartDate}
                    onChange={(e) => {
                      setLogStartDate(e.target.value);
                      fetchActivityLogs();
                    }}
                    className="text-sm"
                  />
                </div>

                {/* End Date */}
                <div>
                  <Label className="text-sm text-slate-600 mb-1">End Date</Label>
                  <Input 
                    type="date"
                    value={logEndDate}
                    onChange={(e) => {
                      setLogEndDate(e.target.value);
                      fetchActivityLogs();
                    }}
                    className="text-sm"
                  />
                </div>
                
                {/* Instructor Filter */}
                <div>
                  <Label className="text-sm text-slate-600 mb-1">Instructor</Label>
                  <Select value={selectedInstructor} onValueChange={(val) => {
                    setSelectedInstructor(val);
                    fetchActivityLogs();
                  }}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Instructors</SelectItem>
                      {users.filter(u => u.role === 'instructor').map(instructor => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Action Filter */}
                <div>
                  <Label className="text-sm text-slate-600 mb-1">Action Type</Label>
                  <Select value={selectedAction} onValueChange={(val) => {
                    setSelectedAction(val);
                    fetchActivityLogs();
                  }}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="created_test">Created Test</SelectItem>
                      <SelectItem value="edited_test">Edited Test</SelectItem>
                      <SelectItem value="deleted_test">Deleted Test</SelectItem>
                      <SelectItem value="assigned_instructor">Assigned Instructor</SelectItem>
                      <SelectItem value="marked_submission">Marked Submission</SelectItem>
                      <SelectItem value="published_result">Published Result</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Activity List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {activityLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No activity logs found</p>
                  </div>
                ) : (
                  activityLogs.map((log, index) => (
                    <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800">
                              {log.user_name}
                            </span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                              {log.user_role}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            <span className="font-medium">{formatAction(log.action)}</span>
                            {log.target_name && <span> → {log.target_name}</span>}
                          </p>
                          {log.details && (
                            <p className="text-xs text-slate-500 mt-1">{log.details}</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <span className="text-xs text-slate-400 block">
                            {new Date(log.timestamp).toLocaleDateString('en-GB')}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(log.timestamp).toLocaleTimeString('en-GB')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Report & Summary Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-slate-700">Report & Summary</h2>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.post('/admin/send-pending-reminders');
                      toast.success(response.data.message);
                    } catch (error) {
                      console.error('Failed to send reminders:', error);
                      toast.error('Failed to send reminder emails');
                    }
                  }}
                  className="btn-primary w-full sm:w-auto text-xs sm:text-sm"
                  size="sm"
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Send Pending Reminders
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.post('/admin/send-pending-whatsapp');
                      if (response.data.success) {
                        toast.success(response.data.message);
                      } else {
                        toast.error(response.data.message);
                      }
                    } catch (error) {
                      console.error('Failed to send WhatsApp reminders:', error);
                      if (error.response?.data?.detail) {
                        toast.error(error.response.data.detail);
                      } else {
                        toast.error('Failed to send WhatsApp reminders');
                      }
                    }
                  }}
                  variant="outline"
                  className="w-full sm:w-auto text-xs sm:text-sm border-green-500 text-green-600 hover:bg-green-50"
                  size="sm"
                >
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Send Pending (WhatsApp)
                </Button>
              </div>
            </div>
            
            {/* Sub-tabs for Report & Summary */}
            <div className="flex gap-2 bg-white rounded-lg p-1 shadow-sm border">
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  reportSubTab === 'summary' 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => setReportSubTab('summary')}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                Summary & Stats
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  reportSubTab === 'activity_log' 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => {
                  setReportSubTab('activity_log');
                  if (isAdmin) {
                    fetchActivityLogs(); // Admin sees all activities
                  } else {
                    fetchInstructorActivityLogs(); // Moderator sees only instructor activities
                  }
                }}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                {isAdmin ? 'Activity Log' : 'Instructor Activity Log'}
              </button>
            </div>
            
            {/* Summary & Stats Sub-tab */}
            {reportSubTab === 'summary' && (
              <>
            {/* Statistics Cards - Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Today's Submissions - NEW */}
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-shadow border-2 border-green-200" 
                onClick={() => {
                  setIsTodaysSubmissionsModalOpen(true);
                  fetchTodaysSubmissions();
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Today&apos;s Submissions</p>
                      <p className="text-3xl font-bold text-green-600">{reportData.todaysSubmissions || 0}</p>
                      <p className="text-xs text-green-500 mt-1">Click to view</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Partial Tests */}
              <Card className="glass-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Partial Tests</p>
                      <p className="text-3xl font-bold text-blue-600">{reportData.totalPartialTests}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Class Tests */}
              <Card className="glass-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Class Tests</p>
                      <p className="text-3xl font-bold text-purple-600">{reportData.totalClassTests}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Pending Submissions - Clickable */}
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-shadow" 
                onClick={() => {
                  setIsPendingSubmissionsModalOpen(true);
                  fetchAllPendingSubmissions();
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Pending Submissions</p>
                      <p className="text-3xl font-bold text-orange-600">{reportData.totalPendingSubmissions}</p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Overdue Pending Submissions Table */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-700 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
                  Overdue Pending Submissions (3+ Days)
                </CardTitle>
                <CardDescription>
                  Submissions that have been pending for more than 3 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reportData.overduePendingSubmissions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No overdue pending submissions!</p>
                    <p className="text-sm text-slate-400 mt-1">All submissions are being processed on time</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Candidate Name</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Title</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Type</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Submitted</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Days Pending</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Assigned Instructor</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.overduePendingSubmissions.map((submission) => (
                          <tr key={submission.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                              {submission.candidate_name}
                              {submission.candidate_no && (
                                <span className="text-xs text-slate-500 ml-2">Batch- {submission.candidate_no}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {submission.test_title}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                submission.test_type === 'partial_test' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {submission.test_type === 'partial_test' ? 'Partial' : 'Class'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {new Date(submission.submitted_at).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-semibold ${
                                submission.days_pending >= 5 ? 'text-red-600' : 'text-orange-600'
                              }`}>
                                {submission.days_pending} days
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {submission.instructor_name || 'Not Assigned'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                                Pending
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            </>
            )}
            
            {/* Activity Log Sub-tab - Admin sees all, Moderator sees only instructors */}
            {reportSubTab === 'activity_log' && (
              <Card className="glass-card">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <CardTitle className="text-lg text-slate-700 flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-blue-600" />
                        {isAdmin ? 'Activity Log' : 'Instructor Activity Log'}
                      </CardTitle>
                      <CardDescription>
                        {isAdmin 
                          ? 'Monitor all user activities: admins, moderators, and instructors'
                          : 'Monitor instructor activities: test creation, marking, publishing, etc.'
                        }
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={isAdmin ? fetchActivityLogs : fetchInstructorActivityLogs}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-4 pb-4 border-b">
                    {/* Instructor filter - only for Moderator */}
                    {!isAdmin && (
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500 mb-1 block">Filter by Instructor</Label>
                        <Select value={selectedInstructorForLog} onValueChange={setSelectedInstructorForLog}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Instructors" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Instructors</SelectItem>
                            {instructorsList.map((instructor) => (
                              <SelectItem key={instructor.id} value={instructor.id}>
                                {instructor.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 mb-1 block">Start Date</Label>
                      <Input 
                        type="date" 
                        value={isAdmin ? logStartDate : instructorLogStartDate}
                        onChange={(e) => isAdmin ? setLogStartDate(e.target.value) : setInstructorLogStartDate(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 mb-1 block">End Date</Label>
                      <Input 
                        type="date" 
                        value={isAdmin ? logEndDate : instructorLogEndDate}
                        onChange={(e) => isAdmin ? setLogEndDate(e.target.value) : setInstructorLogEndDate(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={isAdmin ? fetchActivityLogs : fetchInstructorActivityLogs} size="sm">
                        <Search className="w-4 h-4 mr-2" />
                        Search
                      </Button>
                    </div>
                  </div>
                  
                  {/* Activity Log List */}
                  {(isAdmin ? activityLogs : instructorActivityLogs).length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No activities found</p>
                      <p className="text-sm text-slate-400 mt-1">Click "Refresh" to load activities</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {(isAdmin ? activityLogs : instructorActivityLogs).map((log, index) => (
                        <div 
                          key={index}
                          className="p-3 border rounded-lg hover:bg-slate-50 transition-colors flex items-start gap-3"
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            log.action === 'created_test' || log.action === 'created_class_test' 
                              ? 'bg-green-100 text-green-600' 
                              : log.action === 'marked_submission'
                              ? 'bg-blue-100 text-blue-600'
                              : log.action === 'published_result'
                              ? 'bg-purple-100 text-purple-600'
                              : log.action === 'deleted_test' || log.action === 'deleted_submission'
                              ? 'bg-red-100 text-red-600'
                              : log.action === 'login'
                              ? 'bg-cyan-100 text-cyan-600'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {log.action === 'created_test' || log.action === 'created_class_test' ? (
                              <PlusCircle className="w-5 h-5" />
                            ) : log.action === 'marked_submission' ? (
                              <Edit3 className="w-5 h-5" />
                            ) : log.action === 'published_result' ? (
                              <Send className="w-5 h-5" />
                            ) : log.action === 'deleted_test' || log.action === 'deleted_submission' ? (
                              <Trash2 className="w-5 h-5" />
                            ) : log.action === 'login' ? (
                              <Key className="w-5 h-5" />
                            ) : (
                              <FileText className="w-5 h-5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-700">{log.user_name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                log.user_role === 'instructor' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : log.user_role === 'admin'
                                  ? 'bg-purple-100 text-purple-700'
                                  : log.user_role === 'moderator'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {log.user_role}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">{log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                              {log.target_name && (
                                <span className="text-slate-500"> - {log.target_name}</span>
                              )}
                            </p>
                            {log.details && (
                              <p className="text-xs text-slate-400 mt-1">{log.details}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(log.timestamp).toLocaleString('en-GB')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

      </div>

      {/* Assign Users Dialog */}
      <Dialog open={isAssignUsersOpen} onOpenChange={(open) => {
        setIsAssignUsersOpen(open);
        if (!open) {
          // Clean up state when dialog closes
          setUserAssignments([]);
          setSelectedBatch(null);
          setBatchUsers([]);
        }
      }}>
        <DialogContent className="glass-card border-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700">
              Assign Users to {selectedBatch?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Select users and their roles in this batch
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-3">
            {userAssignments.map((user) => (
              <div key={user.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={user.selected}
                    onCheckedChange={(checked) => 
                      toggleUserSelection(user.user_id, checked, user.current_role)
                    }
                    data-testid={`user-checkbox-${user.user_id}`}
                  />
                  <div>
                    <p className="text-slate-700 font-medium">{user.name}</p>
                    <p className="text-slate-500 text-sm">{user.email}</p>
                  </div>
                </div>
                
                {user.selected && (
                  <Select 
                    value={user.role} 
                    onValueChange={(value) => 
                      toggleUserSelection(user.user_id, true, value)
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleAssignUsers} 
              className="btn-primary"
              data-testid="assign-users-submit"
            >
              Assign Selected Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions Details Modal */}
      <Dialog 
        open={isSubmissionsModalOpen} 
        onOpenChange={(open) => {
          setIsSubmissionsModalOpen(open);
          if (!open) {
            setSelectedBatchFilter('all');
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700">
              Submission Details: {selectedTestSubmissions?.test_title}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              View all submissions for this test
            </DialogDescription>
          </DialogHeader>
          
          {selectedTestSubmissions && (
            <div className="space-y-4">
              {/* Test Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <span className="text-sm text-slate-600">Test Type:</span>
                  <p className="font-medium text-slate-700 capitalize">
                    {selectedTestSubmissions.test_type.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-slate-600">Assigned Instructor:</span>
                  <p className="font-medium text-slate-700">
                    {selectedTestSubmissions.assigned_instructor_name || 'Not Assigned'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-slate-600">Total Submissions:</span>
                  <p className="font-medium text-slate-700">{selectedTestSubmissions.total_submissions}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-600">Batches:</span>
                  <p className="font-medium text-slate-700">
                    {selectedTestSubmissions.batch_names.length > 0 
                      ? selectedTestSubmissions.batch_names.join(', ') 
                      : 'No batches assigned'}
                  </p>
                </div>
              </div>

              {/* Batch Filter */}
              <div className="flex items-center gap-2">
                <Label className="text-slate-700">Filter by Batch:</Label>
                <Select value={selectedBatchFilter} onValueChange={setSelectedBatchFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Batches</SelectItem>
                    {selectedTestSubmissions.batch_names.map((batchName, idx) => (
                      <SelectItem key={idx} value={selectedTestSubmissions.assigned_batches[idx]}>
                        {batchName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Submissions List */}
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-700 mb-2">Submissions:</h4>
                {(() => {
                  // Filter submissions based on selected batch
                  let filteredSubmissions = selectedTestSubmissions.submissions;
                  
                  if (selectedBatchFilter !== 'all') {
                    filteredSubmissions = selectedTestSubmissions.submissions.filter(sub => {
                      // Check if student belongs to selected batch
                      return sub.student_batches && sub.student_batches.includes(selectedBatchFilter);
                    });
                  }

                  return filteredSubmissions.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">
                      {selectedBatchFilter === 'all' ? 'No submissions yet' : 'No submissions in selected batch'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {filteredSubmissions.map((submission) => (
                        <div 
                          key={submission.id} 
                          className="p-3 border rounded-lg bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex flex-col gap-3">
                            {/* Top Section: Student Info + Status */}
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1">
                                <p className="font-medium text-slate-700">
                                  {submission.candidate_name}
                                  {submission.candidate_no && (
                                    <span className="text-xs text-slate-500 ml-2">Batch- {submission.candidate_no}</span>
                                  )}
                                </p>
                                <p className="text-sm text-slate-500">{submission.candidate_email}</p>
                                {submission.submitted_at && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    Submitted: {new Date(submission.submitted_at).toLocaleString('en-GB')}
                                  </p>
                                )}
                              </div>
                              
                              {/* Status Badge */}
                              {submission.is_published ? (
                                <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full whitespace-nowrap bg-emerald-600 text-white font-medium shadow-sm">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Published
                                </span>
                              ) : (
                                <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                                  submission.status === 'submitted' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {submission.status}
                                </span>
                              )}
                            </div>

                            {/* Middle Section: Instructor Assignment */}
                            <div className="flex items-center gap-2 border-t pt-2">
                              <Label className="text-xs text-slate-600 whitespace-nowrap">Assign to:</Label>
                              <Select 
                                value={submission.individually_assigned_instructor || 'bulk'}
                                onValueChange={(value) => handleAssignInstructorToSubmission(submission.id, value)}
                              >
                                <SelectTrigger className="h-8 text-xs flex-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="bulk">
                                    <span className="text-xs">
                                      {selectedTestSubmissions.assigned_instructor_name 
                                        ? `📝 Bulk: ${selectedTestSubmissions.assigned_instructor_name}`
                                        : '📝 Bulk Assignment (None)'}
                                    </span>
                                  </SelectItem>
                                  {instructorsList.map((instructor) => (
                                    <SelectItem key={instructor.id} value={instructor.id}>
                                      <span className="text-xs">
                                        {submission.individually_assigned_instructor === instructor.id ? '✓ ' : ''}
                                        {instructor.name}
                                      </span>
                                    </SelectItem>
                                  ))}
                                  {submission.individually_assigned_instructor && (
                                    <SelectItem value="none">
                                      <span className="text-xs text-red-600">❌ Remove Assignment</span>
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              {submission.individually_assigned_instructor && (
                                <span className="text-xs text-purple-600 font-medium whitespace-nowrap">
                                  ⭐ Individual
                                </span>
                              )}
                            </div>

                            {/* Bottom Section: Action Buttons */}
                            <div className="flex items-center justify-end gap-2 border-t pt-2">
                              {/* Published Link */}
                              {submission.is_published && submission.result_token && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs px-2 py-1.5 min-h-[32px]"
                                  onClick={() => window.open(`/result/${submission.result_token}`, '_blank')}
                                  title="View Result Page"
                                >
                                  <FileText className="w-3 h-3 sm:mr-1" />
                                  <span className="hidden sm:inline">Result</span>
                                </Button>
                              )}
                              
                              {/* Open Button - Admin and Moderator can access */}
                              {(isAdmin || isModerator) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs px-2 py-1.5 min-h-[32px]"
                                  onClick={() => window.location.href = `/instructor/mark/${submission.id}`}
                                  title="View Submission"
                                >
                                  Open
                                </Button>
                              )}
                              
                              {/* Delete Button - Admin or Moderator (if enabled) */}
                              {(isAdmin || (isModerator && moderatorCanDelete)) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-500 hover:bg-red-50 px-2 py-1.5 min-h-[32px]"
                                  onClick={() => {
                                    setDeletingSubmissionId(submission.id);
                                    setIsDeleteConfirmOpen(true);
                                  }}
                                  title="Delete Submission"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{color: '#2E7D32'}}>
                    {selectedTestSubmissions.published_count}
                  </p>
                  <p className="text-sm text-slate-600">Published</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {selectedTestSubmissions.pending_count}
                  </p>
                  <p className="text-sm text-slate-600">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-700">
                    {selectedTestSubmissions.total_submissions}
                  </p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Test & Submissions Confirmation Dialog */}
      <Dialog open={isDeleteTestOpen} onOpenChange={setIsDeleteTestOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center">
              <Trash2 className="w-5 h-5 mr-2" />
              Delete Test or Submissions
            </DialogTitle>
            <DialogDescription>
              <div className="space-y-3 pt-2">
                <div className="p-3 bg-slate-50 rounded border border-slate-200">
                  <div className="font-semibold text-slate-800 mb-1">
                    {selectedTestSubmissions?.test_title}
                  </div>
                  <div className="text-sm text-slate-600">
                    Total submissions: <strong>{selectedTestSubmissions?.total_submissions}</strong>
                  </div>
                </div>
                
                <div className="text-amber-600 text-sm font-medium bg-amber-50 p-2 rounded border border-amber-200">
                  ⚠️ Warning: These actions cannot be undone!
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteTestOpen(false);
                setSelectedTestSubmissions(null);
              }}
              className="w-full"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={handleDeleteTestSubmissions}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Only Submissions (Keep Test)
            </Button>
            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                const confirmMessage = `⚠️ FINAL CONFIRMATION\n\nYou are about to PERMANENTLY delete:\n• Test: "${selectedTestSubmissions?.test_title}"\n• All ${selectedTestSubmissions?.total_submissions} submission(s)\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`;
                
                const userInput = window.prompt(confirmMessage);
                if (userInput === 'DELETE') {
                  await handleDeleteTest();
                } else if (userInput !== null) {
                  toast.error('Deletion cancelled. You must type "DELETE" to confirm.');
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Test + All Submissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Submission Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Submission</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this submission? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setDeletingSubmissionId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteSubmission}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={isDeleteUserOpen} onOpenChange={setIsDeleteUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteUserOpen(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteUser}
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-700">Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for <strong>{selectedUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-700">New Password</Label>
              <Input
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-modern"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsResetPasswordOpen(false);
                setSelectedUser(null);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleResetPasswordSubmit}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Register Students Dialog */}
      <Dialog open={isBulkRegisterOpen} onOpenChange={setIsBulkRegisterOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700">Bulk Register Students</DialogTitle>
            <DialogDescription>
              Add multiple students to <strong>{selectedBatch?.name}</strong> batch
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* CSV Input */}
            <div className="space-y-2">
              <Label className="text-slate-700">
                CSV Data <span className="text-xs text-slate-500">(email, name, phone, password)</span>
              </Label>
              <textarea
                placeholder="student1@email.com, John Doe, 01712345678, password123&#10;student2@email.com, Jane Smith, 01798765432, pass456"
                value={bulkCSV}
                onChange={(e) => setBulkCSV(e.target.value)}
                rows={6}
                className="w-full p-3 border rounded-lg text-sm font-mono"
              />
              <p className="text-xs text-slate-500">
                Format: email, name, phone, password (one student per line)
              </p>
            </div>

            {/* Credit Allocation */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Credit Allocation (per student)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Listening</Label>
                  <Input
                    type="number"
                    min="0"
                    value={credits.listening}
                    onChange={(e) => setCredits({...credits, listening: parseInt(e.target.value) || 0})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Reading</Label>
                  <Input
                    type="number"
                    min="0"
                    value={credits.reading}
                    onChange={(e) => setCredits({...credits, reading: parseInt(e.target.value) || 0})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Writing</Label>
                  <Input
                    type="number"
                    min="0"
                    value={credits.writing}
                    onChange={(e) => setCredits({...credits, writing: parseInt(e.target.value) || 0})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Speaking</Label>
                  <Input
                    type="number"
                    min="0"
                    value={credits.speaking}
                    onChange={(e) => setCredits({...credits, speaking: parseInt(e.target.value) || 0})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs text-slate-600">Mock Test</Label>
                  <Input
                    type="number"
                    min="0"
                    value={credits.mock}
                    onChange={(e) => setCredits({...credits, mock: parseInt(e.target.value) || 0})}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Total Credits */}
              <div className="pt-2 border-t border-slate-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-700">Total Credits per Student:</span>
                  <span className="text-lg font-bold" style={{color: 'rgb(22, 113, 127)'}}>
                    {Object.values(credits).reduce((a, b) => a + b, 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkRegisterOpen(false);
                setBulkCSV('');
                setCredits({ listening: 0, reading: 0, writing: 0, speaking: 0, mock: 0 });
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleBulkRegister}
            >
              Register Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Students List Modal */}
      <Dialog open={isStudentListOpen} onOpenChange={setIsStudentListOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700">
              Students in {selectedBatch?.name}
            </DialogTitle>
            <DialogDescription>
              Manage students enrolled in this batch
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {batchStudents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No students in this batch yet
              </div>
            ) : (
              batchStudents.map((student) => (
                <div key={student.user_id} className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-700">{student.name}</h4>
                      <p className="text-sm text-slate-600">{student.email}</p>
                      {student.phone && (
                        <p className="text-xs text-slate-500 mt-1">{student.phone}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-600 border-purple-300"
                        onClick={() => {
                          setEditingStudent(student);
                          setNewPassword('');
                          setIsResetPasswordOpen(true);
                        }}
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => handleRemoveStudentFromBatch(student.user_id)}
                        title="Remove from Batch"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStudentListOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Settings Modal */}
      <Dialog open={isBatchSettingsOpen} onOpenChange={setIsBatchSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-700">Batch Settings</DialogTitle>
            <DialogDescription>
              Configure settings for {selectedBatch?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Portal Validity (Until)</Label>
              <Input
                type="date"
                value={portalValidity}
                onChange={(e) => setPortalValidity(e.target.value)}
                className="input-modern"
                placeholder="Select date"
              />
              <p className="text-xs text-slate-500">
                Students will not be able to access portal after this date
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBatchSettingsOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleUpdateBatchSettings}
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
        <DialogContent className="glass-card border-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-700">
              Edit Profile - {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <Input
                placeholder="Enter full name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="input-modern"
              />
            </div>

            {/* Email - Admin Only */}
            {isAdmin && (
              <div className="space-y-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  Email Address
                </Label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="input-modern"
                />
                {profileData.email !== selectedUser?.email && profileData.email && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Changing email will send a notification to the new address
                  </p>
                )}
              </div>
            )}

            {/* Profile Picture */}
            <div className="space-y-2">
              <Label className="text-slate-700">Profile Picture</Label>
              <div className="flex items-center space-x-4">
                {profileData.profile_picture ? (
                  <img
                    src={profileData.profile_picture}
                    alt="Preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-slate-300"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">
                      {selectedUser?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageUpload}
                    className="hidden"
                    id="profile-pic-upload"
                  />
                  <label htmlFor="profile-pic-upload">
                    <Button type="button" variant="outline" asChild>
                      <span className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Photo
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">Max 2MB, JPG/PNG</p>
                </div>
              </div>
            </div>

            {/* Designation */}
            <div className="space-y-2">
              <Label className="text-slate-700">Designation</Label>
              <Input
                placeholder="e.g., Senior Instructor, IELTS Expert"
                value={profileData.designation}
                onChange={(e) => setProfileData({ ...profileData, designation: e.target.value })}
                className="input-modern"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-slate-700">Phone Number</Label>
              <Input
                placeholder="e.g., +880 1234567890"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="input-modern"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label className="text-slate-700">Bio / About</Label>
              <textarea
                placeholder="Brief description about the instructor..."
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                className="input-modern min-h-[100px]"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={handleUpdateProfile} className="btn-primary">
              Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Pending Submissions Modal */}
      <Dialog open={isPendingSubmissionsModalOpen} onOpenChange={setIsPendingSubmissionsModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              All Pending Submissions
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              View all submissions that are pending for marking
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {allPendingSubmissions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>No pending submissions!</p>
                <p className="text-sm text-slate-400 mt-1">All submissions have been marked</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Candidate Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Title</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Submitted</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Time</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Assigned Instructor</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPendingSubmissions.map((submission) => (
                      <tr key={submission.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                          {submission.candidate_name}
                          {submission.candidate_no && (
                            <span className="text-xs text-slate-500 ml-2">Batch- {submission.candidate_no}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {submission.test_title}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            submission.test_type === 'partial_test' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {submission.test_type === 'partial_test' ? 'Partial' : 'Class'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {submission.submitted_at 
                            ? new Date(submission.submitted_at).toLocaleDateString('en-GB')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {submission.submitted_at 
                            ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-red-600">
                          {submission.instructor_name || 'Not Assigned'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

        {/* Failed Emails Tab */}
        {activeTab === 'failed_emails' && (isAdmin || (isModerator && moderatorCanViewFailedEmails)) && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-red-600" />
                    Failed Email Notifications
                  </CardTitle>
                  <CardDescription>
                    Published results where email could not be sent to students. Click "Resend" to try again.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchFailedEmails}
                    disabled={loadingFailedEmails}
                  >
                    <Clock className={`w-4 h-4 mr-2 ${loadingFailedEmails ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  {selectedFailedEmails.length > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={handleBulkResendEmails}
                        disabled={resendingEmails}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {resendingEmails ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Resend Selected ({selectedFailedEmails.length})
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to remove ${selectedFailedEmails.length} emails from this list? This will not delete the submissions.`)) {
                            try {
                              await axios.post('/admin/bulk-delete-failed-emails', selectedFailedEmails);
                              toast.success(`Removed ${selectedFailedEmails.length} emails from list`);
                              fetchFailedEmails();
                              setSelectedFailedEmails([]);
                            } catch (error) {
                              toast.error('Failed to remove emails');
                            }
                          }
                        }}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Selected
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Rate Limit Info */}
              {failedEmails.length > 10 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Note:</strong> With your 1000 emails/day plan, bulk sending will have a 2-second delay between emails to ensure smooth delivery.
                  </p>
                </div>
              )}
              {loadingFailedEmails ? (
                <div className="text-center py-8">
                  <Clock className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-500">Loading failed emails...</p>
                </div>
              ) : failedEmails.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  <p className="text-slate-600 font-medium">All emails sent successfully!</p>
                  <p className="text-sm text-slate-400">No failed email notifications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Select All */}
                  <div className="flex items-center gap-2 pb-3 border-b">
                    <Checkbox
                      checked={selectedFailedEmails.length === failedEmails.length && failedEmails.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedFailedEmails(failedEmails.map(f => f.id));
                        } else {
                          setSelectedFailedEmails([]);
                        }
                      }}
                    />
                    <span className="text-sm text-slate-600">Select All ({failedEmails.length})</span>
                  </div>
                  
                  {/* Failed Email List */}
                  {failedEmails.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <Checkbox
                        checked={selectedFailedEmails.includes(item.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedFailedEmails([...selectedFailedEmails, item.id]);
                          } else {
                            setSelectedFailedEmails(selectedFailedEmails.filter(id => id !== item.id));
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-700">{item.candidate_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.email_status === 'failed' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.email_status === 'failed' ? 'Failed' : 'Pending'}
                          </span>
                          {item.email_retry_count > 0 && (
                            <span className="text-xs text-slate-400">
                              ({item.email_retry_count} retries)
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">{item.candidate_email}</div>
                        <div className="text-xs text-slate-400 mt-1">
                          Test: {item.test_title}
                        </div>
                        {item.submitted_at && (
                          <div className="text-xs text-slate-400">
                            Submitted: {new Date(item.submitted_at).toLocaleString('en-GB')}
                          </div>
                        )}
                        {item.email_error && (
                          <div className="text-xs text-red-500 mt-1">
                            Error: {item.email_error}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.post(`/admin/submissions/${item.id}/resend-email`);
                              toast.success('Email sent successfully');
                              fetchFailedEmails();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to send email');
                            }
                          }}
                          className="text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Resend
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Remove this email from the list?')) {
                              try {
                                await axios.delete(`/admin/failed-emails/${item.id}`);
                                toast.success('Removed from list');
                                fetchFailedEmails();
                              } catch (error) {
                                toast.error('Failed to remove');
                              }
                            }
                          }}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

      {/* Today's Submissions Modal */}
      <Dialog open={isTodaysSubmissionsModalOpen} onOpenChange={setIsTodaysSubmissionsModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-700 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-green-600" />
              Today&apos;s Submissions ({new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })})
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              All submissions received today (Partial Tests + Class Tests)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {todaysSubmissionsList.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No submissions today!</p>
                <p className="text-sm text-slate-400 mt-1">Check back later for new submissions</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{todaysSubmissionsList.length}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {todaysSubmissionsList.filter(s => s.test_type === 'partial_test').length}
                    </p>
                    <p className="text-xs text-slate-500">Partial Tests</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {todaysSubmissionsList.filter(s => s.test_type === 'class_test').length}
                    </p>
                    <p className="text-xs text-slate-500">Class Tests</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">
                      {todaysSubmissionsList.filter(s => s.status === 'submitted').length}
                    </p>
                    <p className="text-xs text-slate-500">Pending</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Candidate Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Title</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Test Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Time</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Assigned To</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todaysSubmissionsList.map((submission) => (
                        <tr key={submission.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                            {submission.candidate_name}
                            {submission.candidate_no && (
                              <span className="text-xs text-slate-500 ml-2">Batch- {submission.candidate_no}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {submission.test_title}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              submission.test_type === 'partial_test' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {submission.test_type === 'partial_test' ? 'Partial' : 'Class'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {submission.submitted_at 
                              ? new Date(submission.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {submission.instructor_name ? (
                              <span className="text-green-600 font-medium">{submission.instructor_name}</span>
                            ) : (
                              <span className="text-red-500">Not Assigned</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              submission.status === 'submitted' ? 'bg-yellow-100 text-yellow-700' :
                              submission.status === 'marked' ? 'bg-blue-100 text-blue-700' :
                              submission.status === 'published' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {submission.status?.charAt(0).toUpperCase() + submission.status?.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;